// A mesa da partida, no desenho do protótipo. Tudo vem da visão que o servidor manda para o seu lugar;
// o aparelho só pede (jogar, truco, votar) e anima o que chega.
import { ChipLimite, FaixaLimite, FolhaLimite, LimiteNoFim, useFaixaDoLimite, useGratisNaMesa } from './LimiteNaMesa.tsx';
import { avisoDoLimiteVisto, destinoAoSairDaMesa } from '../comum/limite.ts';
import { ligaNoAr } from '../comum/liga.ts';
import { useEffect, useRef, useState } from 'react';
import { SINAIS, estadoDaRodada, next, nextLevel, partners, podeEsconderNaRodada, quemFecha, rankOf, sinalDe, suitOf, team } from '@truco6/regras';
import type { Card, Gesture, PlayerView, Seat, Suit, Team } from '@truco6/regras';
import type { EventoNumerado, LugarPublico, SalaPublica } from '@truco6/mesa/protocolo';
import { PRAZO_REVANCHE_S } from '@truco6/planos';
import { imagemDaCarta, nomeDoId } from '@truco6/ui';
import { Coroa, Rosto } from '../comum/Rosto.tsx';
import { comPrazo, linhaDoPote } from '../comum/pote.ts';
import type { Modo } from '../comum/Palco.tsx';
import { CartaoJogador, PessoasDaMesa } from './CartaoJogador.tsx';
import { conta } from '../comum/conta.ts';
import { medirUmaVez } from '../comum/medicao.ts';
import { GRITO_DO_NIVEL, gritar, type Grito } from '../som/gritos.ts';
import { mudarPreferencias, usePreferenciasSom } from '../som/preferencias.ts';
import { decodificar, som, vibrar } from '../som/som.ts';
import { ir } from '../comum/rotas.ts';
import { vencedorDoConfronto } from '../liga/campeonatos.tsx';
import { BadgesNoFim } from '../liga/badges.tsx';
import { estiloDaMesa, versoDoCosmetico } from '../comum/cosmeticos.tsx';
import { type PoteDaPartida, acaoDoFim } from '@truco6/gamificacao';
import { MissoesDaPartida } from '../liga/missoes.tsx';
import { PasseDaPartida } from '../liga/passe.tsx';
import { OfensivaDaPartida } from '../liga/ofensiva.tsx';
import { RodagemDaPartida, nomeDoLance } from '../liga/rodagem.tsx';
import { EntradaPatrocinada, IconeTrofeu, LigaNoFim, MarcaNoFeltro, versoDaLiga } from '../liga/comum.tsx';
import { agoraNoServidor, useAgora, useMesa } from '../comum/useMesa.ts';
import { FRASES, INTERVALO_FRASE_MS, INTERVALO_REACAO_PLATEIA_MS, ZOEIRA } from '@truco6/mesa/protocolo';
import type { ConexaoMesa, EstadoMesa, FonteDaMesa } from './conexao.ts';
import { DISTRIBUIR, DURACAO_DISTRIBUICAO, PAUSA_RODADA, RECOLHER, movimentoReduzido, voarCarta } from './animacao.ts';
import {
  ATALHOS_COMPUTADOR, CARTA_NA_MAO, JOGADA, MONTE, ONZE, PALCO_COMPUTADOR, ROTACAO, TRUCO_COMPUTADOR, type Ponto, centroNaMao, escalaDaMao, lugares,
  maoAlvo, monteDe, posicaoNaMao,
} from './geometria.ts';
import { HistoricoDaMao, SeuTrio, type SinalRecebido, useEscalaDoCentro } from './Computador.tsx';
import {
  CARTA_DO_SINAL, GESTO_EMOJI, NAIPE_DA_MANILHA, NOME_DO_NAIPE, PEDIDO, REACOES, REGRAS, SIMBOLO, VOTO_EMOJI, apelidoDe, avisoDeQueda, avisoDeVaga,
  ehBot, ehVaga, esperaAMao, frase, pontos, rostoDoLugar, trioDaPlateia,
} from './textos.ts';

type Folha = 'sinais' | 'reacoes' | 'regras' | 'som' | 'menu' | 'jogador' | 'plateia' | 'reacoes-plateia' | 'limite' | null;
interface ReacaoDaPlateia { id: number; emoji: string }
interface Emo { id: number; rel: number; emoji: string; naipe: Suit | null; rotulo: string }
interface Balao { id: number; rel: number; texto: string; tipo: 'frase' | 'aviso'; nosso: boolean; rotulo?: string }
interface Momento { id: number; grande: string; linha: string; cls: string; selo: boolean; fala: string }
interface Distribuicao { chave: string; dealer: Seat; fase: 'embaralhar' | 'dando'; dadas: number[] }
interface Voo { fx: number; fy: number; fs: number; fo: number } // de onde a carta jogada vem, em relação à mesa

let contador = 0;
const pad = (n: number) => (n < 10 ? '0' : '') + n;
// Turquesa e coral vêm sempre com NÓS e ELES. Quem assiste não é de trio nenhum: dourado e creme.
const corDoTrio = (t: Team, meu: Team, assistindo = false) => (assistindo
  ? (t === meu ? 'var(--gold-400)' : 'var(--cream-050)')
  : (t === meu ? 'var(--nos-400)' : 'var(--eles-400)'));
const clsDoTrio = (t: Team, meu: Team) => (t === meu ? 'n' : 'e');

const chaveDoVoo = (mao: number, rodada: number, s: Seat) => mao + ':' + rodada + ':' + s;
// Variáveis CSS (--fx, --rot...) no style do React.
const varsCss = (v: Record<string, string>) => v as React.CSSProperties;
// Em pé, durante a votação do seu trio, a mão sobe para a doca cobrir só a parte de baixo das cartas. No computador,
// a doca fica no lugar do TRUCO e embaixo das cartas, e a mão sobe mais.
const MAO_SOBE_NA_VOTACAO = 18;
const MAO_SOBE_NO_COMPUTADOR = 40;

export const TRAVA_DO_SINAL_MS = 2000; // depois de um sinal, os gestos ficam travados

function nomeNoLugar(sala: SalaPublica, eu: Seat, s: Seat, assistindo = false): string {
  return s === eu && !assistindo ? 'Você' : apelidoDe(sala.lugares[s]);
}

// As abas de frases prontas: Frases e Zoeira. O aparelho lembra a última; na primeira vez, Frases.
type AbaFrases = 'frases' | 'zoeira';
const CHAVE_ABA_FRASES = 'truco6:aba-frases';
function lerAbaFrases(): AbaFrases {
  try { return localStorage.getItem(CHAVE_ABA_FRASES) === 'zoeira' ? 'zoeira' : 'frases'; } catch { return 'frases'; }
}

// A mesa de quem assiste: sem mão e sem botões de jogo. Na barra, as reações da plateia, Sentar na mesa (quando há
// lugar na sala de espera) e Sair.
export interface ModoPlateia {
  quantos: number;
  podeSentar: boolean;
  aoSentar: () => void;
  aoSair: () => void;
  reagir: (emoji: string) => boolean;
}


// aoChamarAmigos: com vaga aberta, o cartão do bot no lugar abre a folha de chamar amigos (conta Google).
// plateia: a mesa de quem assiste (ConexaoPlateia). eDono: o dono da sala privada tira alguém da plateia.
// modo: 'computador' monta a grade de 3 colunas da tela 5.5 (Palco com computador); sem ele, em pé ou deitado.
// assinar: o Checkout do Bronze em outra aba (a folha "Jogue sem limite"), com a mesa conectada nesta.
export function Mesa({ c, paisagem, modo: modoDoPalco, lerSaldo, lerPote, lerConvite, aoChamarAmigos, plateia, assinar }: {
  c: FonteDaMesa; paisagem: boolean; modo?: Modo; lerSaldo?: () => Promise<number>; lerPote?: LerPote; lerConvite?: () => Promise<string | null>;
  aoChamarAmigos?: () => void; plateia?: ModoPlateia; assinar?: () => Promise<void>;
}) {
  const e = useMesa(c);
  const assistindo = !!plateia;
  const modo: Modo = modoDoPalco ?? (paisagem ? 'deitado' : 'pe'), pc = modo === 'computador';
  // Quem assiste no computador não tem mão: o lugar de baixo fica embaixo da mesa, como deitado.
  const geo: Modo = pc && assistindo ? 'deitado' : modo;
  const assistindoRef = useRef(assistindo);
  assistindoRef.current = assistindo;
  // Reações da plateia: a faixa Plateia, para quem joga e para quem assiste. Cada uma fica 3 s.
  const [reacoesPlateia, setReacoesPlateia] = useState<ReacaoDaPlateia[]>([]);
  const [reacaoAte, setReacaoAte] = useState(0);
  useAgora(); // redesenha os relógios; a hora vem de agoraNoServidor, nunca atrasada
  const agora = agoraNoServidor(e);
  const [sel, setSel] = useState<number | null>(null);
  const [esconder, setEsconder] = useState(false);
  const [folha, setFolha] = useState<Folha>(null);
  // Limite do Grátis: o chip junto do placar, a faixa do começo da partida e a folha "Jogue sem limite".
  const gratisHoje = useGratisNaMesa(), faixaDoLimite = useFaixaDoLimite(e);
  const gratis = assistindo || e.eu === null ? null : gratisHoje;
  const chipLimite = gratis !== null && <ChipLimite restantes={gratis} aoAbrir={() => setFolha('limite')} />;
  const [verJogador, setVerJogador] = useState<Seat | null>(null); // o cartão do jogador aberto (folha 'jogador')
  const [sinalEnviado, setSinalEnviado] = useState<Gesture | null>(null); // marcado, com os gestos travados por 2 s
  const [emos, setEmos] = useState<Emo[]>([]);
  const [baloes, setBaloes] = useState<Balao[]>([]);
  const [fraseAte, setFraseAte] = useState(0); // 5 s entre frases, somando as abas Frases e Zoeira
  const [abaFrases, setAbaFrases] = useState<AbaFrases>(lerAbaFrases);
  const [toast, setToast] = useState<{ id: number; texto: string } | null>(null);
  // Aviso de quem caiu e da vaga aberta (canvas 5.3 e 5.3b): o cartão no centro da mesa, por cima do toast.
  const [avisoMesa, setAvisoMesa] = useState<{ id: number; texto: string } | null>(null);
  const mostrarAviso = (texto: string) => {
    const id = ++contador;
    setAvisoMesa({ id, texto });
    setTimeout(() => setAvisoMesa((a) => (a?.id === id ? null : a)), 5000);
  };
  const [momento, setMomento] = useState<Momento | null>(null);
  const [pausa, setPausa] = useState<number | null>(null);
  // Rodagem que sobe na hora do lance: o "+N" no seu rosto por 1,8 s.
  const [subida, setSubida] = useState<{ id: number; quanto: number; lance: string } | null>(null);
  const aoVivo = e.rodagemAoVivo;
  useEffect(() => {
    if (!aoVivo || aoVivo.partida !== e.partida) return;
    setSubida({ id: aoVivo.id, quanto: aoVivo.quanto, lance: aoVivo.lance });
    const t = setTimeout(() => setSubida((x) => (x?.id === aoVivo.id ? null : x)), 1800);
    return () => clearTimeout(t);
  }, [aoVivo?.id]);
  const [dist, setDist] = useState<Distribuicao | null>(null);
  const [pouso, setPouso] = useState<Seat | null>(null); // o monte pousa no centro, vindo de quem deu
  const [voos, setVoos] = useState<Record<string, Voo>>({});
  const [recolher, setRecolher] = useState<{ rodada: number; para: Seat | null } | null>(null);
  const estadoRef = useRef<EstadoMesa>(e);
  estadoRef.current = e;
  const geoRef = useRef(geo);
  geoRef.current = geo;
  // De onde voam as cartas do lugar de baixo: a sua mão, ou o rosto de quem senta embaixo para quem assiste.
  const alvoDaMao = () => (assistindoRef.current ? lugares(geoRef.current)[0]! : maoAlvo(geoRef.current));
  const [escalaDoCentro, centroRef] = useEscalaDoCentro(PALCO_COMPUTADOR.largura, PALCO_COMPUTADOR.altura);
  // Os sinais que você recebeu nesta mão (o histórico da mão, no computador).
  const [sinaisRecebidos, setSinaisRecebidos] = useState<(SinalRecebido & { mao: number })[]>([]);
  const caixaRef = useRef<HTMLDivElement>(null); // cartas em voo; o React não desenha nada aqui
  const origemMinha = useRef<Ponto | null>(null); // de onde saiu a carta que você acabou de jogar
  const distribuidas = useRef(new Set<string>());
  const gravacoes = useRef(new Map<string, AudioBuffer | null>()); // 'usuario:grito:versão'
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);
  const agendar = (fn: () => void, ms: number) => { timers.current.push(setTimeout(fn, ms)); };
  useEffect(() => () => { for (const t of timers.current) clearTimeout(t); }, []);

  // ---- efeitos que vêm dos eventos ----
  useEffect(() => {
    const toastar = (texto: string, ms = 2200) => {
      const id = ++contador;
      setToast({ id, texto });
      setTimeout(() => setToast((t) => (t?.id === id ? null : t)), ms);
    };
    // fala: o que o leitor de tela anuncia ("Rafa pediu SEIS!"); sem ela, a linha.
    const momentar = (grande: string, linha: string, cls: string, selo = false, fala = linha) => {
      const id = ++contador;
      setMomento({ id, grande, linha, cls, selo, fala });
      setTimeout(() => setMomento((m) => (m?.id === id ? null : m)), 1100);
    };
    // Grito de quem pediu: a gravação dele (se tiver e já baixou) ou a voz padrão.
    const gritarDe = (sala: SalaPublica, s: Seat, nivel: number, r: number) => {
      const l = sala.lugares[s], g = GRITO_DO_NIVEL[nivel];
      const ver = l?.tipo === 'pessoa' && !l.bot && g ? l.perfil.gritos?.[g] : undefined;
      gritar(nivel, r, ver && l?.tipo === 'pessoa' ? gravacoes.current.get(`${l.id}:${g}:${ver}`) ?? null : null);
    };
    const emo = (rel: number, emoji: string, rotulo: string, naipe: Suit | null = null, ms = 2400) => {
      const id = ++contador;
      setEmos((l) => [...l.filter((x) => x.rel !== rel), { id, rel, emoji, naipe, rotulo }]);
      setTimeout(() => setEmos((l) => l.filter((x) => x.id !== id)), ms);
    };
    // Balão de texto sobre o rosto (aviso de sinal, frases prontas e zoeira).
    const balao = (rel: number, texto: string, ms: number, tipo: Balao['tipo'], nosso: boolean, rotulo?: string) => {
      const id = ++contador;
      setBaloes((l) => [...l.filter((x) => x.rel !== rel), { id, rel, texto, tipo, nosso, ...(rotulo ? { rotulo } : {}) }]);
      setTimeout(() => setBaloes((l) => l.filter((x) => x.id !== id)), ms);
    };
    const tirarEventos = c.aoEventos((evs: EventoNumerado[], est: EstadoMesa) => {
      const eu = est.eu, sala = est.sala, v = est.visao, vendo = assistindoRef.current;
      if (eu === null || !sala || !v) return;
      const meu = team(eu), rel = (s: Seat) => (s - eu + 6) % 6, nome = (s: Seat) => nomeNoLugar(sala, eu, s, vendo);
      // "Nós aceitamos" e "Eles aceitaram" para quem joga; "Trio de Bia aceitou" para quem assiste.
      const fr = (t: Team, nos: string, eles: string, um: string) => (vendo ? trioDaPlateia(sala.lugares, t) + ' ' + um : frase(t, meu, nos, eles));
      for (const { ev } of evs) {
        switch (ev.type) {
          case 'hand_start':
            setPausa(null); setRecolher(null); setVoos({});
            if (ev.special) setTimeout(som.especial, 300);
            if (ev.special === 'onze' && vendo && ev.onzeTeam !== null) toastar(`Mão de onze. ${trioDaPlateia(sala.lugares, ev.onzeTeam)} decide se joga.`, 2800);
            else if (ev.special === 'onze' && ev.onzeTeam !== meu) toastar('Mão de onze para Eles. Eles decidem se jogam.', 2800);
            else if (ev.special === 'ferro') toastar('Mão de ferro. Quem vencer leva a partida.', 3400);
            break;
          case 'play': {
            // A carta voa de onde saiu: do rosto de quem jogou, ou da posição dela na sua mão.
            const r = rel(ev.seat), alvo = JOGADA[r]!;
            const de: Ponto = r === 0 ? origemMinha.current ?? alvoDaMao() : lugares(geoRef.current)[r]!;
            if (r === 0) origemMinha.current = null;
            if (!movimentoReduzido()) {
              const daMao = r === 0 && !vendo;
              const voo: Voo = { fx: de[0] - alvo[0] - 26, fy: de[1] - alvo[1] - 36, fs: daMao ? escalaDaMao(geoRef.current) : 0.6, fo: daMao ? 1 : 0.2 };
              setVoos((m) => ({ ...m, [chaveDoVoo(v.handNo, ev.round, ev.seat)]: voo }));
            }
            // o tapa na mesa vem quando a carta chega
            setTimeout(ev.hidden ? som.cartaCoberta : som.carta, movimentoReduzido() ? 0 : 430);
            if (ev.card && !ev.hidden && v.hand && v.hand.special !== 'ferro' && rankOf(ev.card) === v.hand.manilha && suitOf(ev.card) === 'c') {
              momentar('ZAP!', ev.seat === eu && !vendo ? 'Você jogou o Zap' : nome(ev.seat) + ' jogou o Zap', clsDoTrio(team(ev.seat), meu), true);
              setTimeout(som.zap, 300);
            }
            break;
          }
          case 'truco':
            gritarDe(sala, ev.seat, ev.level, rel(ev.seat));
            if (team(ev.seat) !== meu && !vendo) vibrar(120);
            emo(rel(ev.seat), '📣', PEDIDO[ev.level]!);
            momentar(PEDIDO[ev.level] + '!', nome(ev.seat) + ' pediu', clsDoTrio(team(ev.seat), meu), false, nome(ev.seat) + ' pediu ' + PEDIDO[ev.level] + '!');
            break;
          case 'raise':
            gritarDe(sala, ev.seat, ev.level, rel(ev.seat));
            if (team(ev.seat) !== meu && !vendo) vibrar(120);
            emo(rel(ev.seat), '📣', PEDIDO[ev.level]!);
            momentar(PEDIDO[ev.level] + '!', nome(ev.seat) + ' pediu', clsDoTrio(team(ev.seat), meu), false, nome(ev.seat) + ' pediu ' + PEDIDO[ev.level] + '!');
            break;
          case 'accept':
            som.aceitou();
            emo(rel(ev.seat), '👍', 'aceitou');
            toastar(fr(team(ev.seat), 'aceitamos', 'aceitaram', 'aceitou') + '. Mão vale ' + ev.level + '.');
            break;
          case 'run':
            som.correu();
            emo(rel(ev.seat), '🏃', 'correu');
            break;
          case 'onze_play':
            toastar(fr(ev.team, 'vamos jogar', 'vão jogar', 'vai jogar') + ' a mão de onze. Vale 3.');
            break;
          case 'round_end': {
            setPausa(ev.round);
            setTimeout(() => (ev.result === 'tie' ? som.empachou() : som.rodada(vendo || ev.result === meu)), 450);
            const para = ev.result === 'tie' ? null : ev.winnerSeat;
            setTimeout(() => setRecolher({ rodada: ev.round, para }), PAUSA_RODADA - RECOLHER);
            setTimeout(() => { setPausa((p) => (p === ev.round ? null : p)); setRecolher(null); }, PAUSA_RODADA);
            if (ev.result === 'tie') {
              const r0 = v.hand?.rounds[0]?.result, mao = nome(v.mao);
              toastar(ev.round === 0 ? `Empachou! A carta maior da segunda decide. ${mao} abre.`
                : ev.round === 1 && r0 === 'tie' ? `Empachou de novo! Vai para a última. ${mao} abre.`
                  : 'Empachou. Vale a primeira rodada.', 2000);
            }
            break;
          }
          case 'hand_end': {
            let msg: string;
            if (ev.team === null) msg = 'As três rodadas empacharam. Ninguém pontua.';
            else if (ev.reason === 'run') msg = fr((1 - ev.team) as Team, 'corremos', 'correram', 'correu') + '. ' + fr(ev.team, 'levamos', 'levam', 'leva') + ' ' + pontos(ev.points);
            else if (ev.reason === 'onze_run') msg = fr((1 - ev.team) as Team, 'corremos', 'correram', 'correu') + ' da mão de onze. ' + fr(ev.team, 'levamos', 'levam', 'leva') + ' 1 ponto.';
            else msg = fr(ev.team, 'levamos', 'levam', 'leva') + ' ' + pontos(ev.points);
            setTimeout(() => toastar(msg, 2200), 700);
            break;
          }
          case 'signal':
            som.pop();
            if (ev.gesture && !vendo && ev.to?.includes(eu)) {
              const sinal = { id: ++contador, seat: ev.seat, gesture: ev.gesture, mao: v.handNo };
              setSinaisRecebidos((l) => [...l.filter((x) => x.mao === sinal.mao), sinal]);
            }
            // Só o aviso: o adversário sabe só que passou sinal. Trio (ou Todos veem): o gesto, que vai sempre para os dois.
            if (!ev.gesture) balao(rel(ev.seat), nome(ev.seat) + ' passou sinal', 2000, 'aviso', team(ev.seat) === meu);
            else emo(rel(ev.seat), GESTO_EMOJI[ev.gesture], CARTA_DO_SINAL[ev.gesture], sinalDe(ev.gesture).naipe ?? null, 2800);
            break;
        }
      }
    });
    const tirarRecados = c.aoRecado((m) => {
      const eu = estadoRef.current.eu;
      if (m.t === 'reacao_plateia') {
        const id = ++contador;
        setReacoesPlateia((l) => [...l.slice(-2), { id, emoji: m.emoji }]);
        setTimeout(() => setReacoesPlateia((l) => l.filter((x) => x.id !== id)), 3000);
        return;
      }
      if (eu === null) return;
      // Alguém sentou na vaga aberta: todos veem por 3 s, menos quem sentou (vê a faixa da próxima mão).
      if (m.t === 'sentou') { if (m.lugar !== eu) toastar(`${m.apelido} sentou no lugar do ${m.no}`, 3000); return; }
      som.pop();
      // Frase e zoeira: o mesmo balão, com a borda do trio de quem mandou. Leitor de tela: "Bia mandou: Freguês!".
      const sala = estadoRef.current.sala;
      if (m.t === 'frase') balao((m.lugar - eu + 6) % 6, m.frase, 3000, 'frase', team(m.lugar) === team(eu), sala ? `${nomeNoLugar(sala, eu, m.lugar)} mandou: ${m.frase}` : undefined);
      else emo((m.lugar - eu + 6) % 6, m.emoji, m.t === 'opiniao' ? 'opinião' : 'reação');
    });
    return () => { tirarEventos(); tirarRecados(); };
  }, [c]);

  // Ação recusada pelo servidor vira aviso na mesa.
  useEffect(() => {
    if (!e.aviso) return;
    const id = ++contador;
    setToast({ id, texto: e.aviso.mensagem });
    setTimeout(() => setToast((t) => (t?.id === id ? null : t)), 2200);
  }, [e.aviso]);

  const v = e.visao, sala = e.sala, eu = e.eu;
  const h = v?.hand ?? null;
  // Liga patrocinada: o verso das cartas é o da liga; na sala privada, o do dono; senão, o da casa.
  const verso = sala?.liga ? versoDaLiga(sala.liga) : versoDoCosmetico(sala?.visual?.verso);

  // Alguém caiu: avisa uma vez por queda. O lugar mostra a contagem até o bot assumir. Na sala valendo Tentos, o
  // lugar não abre: o bot joga até a pessoa voltar.
  const quedas = e.prazos.filter((p) => p.tipo === 'reconexao' && p.lugar !== undefined && (p.lugar !== eu || assistindo))
    .map((p) => p.lugar + ':' + p.ate).join(',');
  const quedasVistas = useRef(new Set<string>());
  useEffect(() => {
    if (!quedas || !sala || eu === null) return;
    for (const k of quedas.split(',')) {
      if (quedasVistas.current.has(k)) continue;
      quedasVistas.current.add(k);
      const [s, ate] = k.split(':').map(Number) as [Seat, number];
      const l = sala.lugares[s], bot = l?.tipo === 'pessoa' && l.botApelido ? l.botApelido : 'bot';
      if (sala.fase === 'jogo' && !sala.tentos) { mostrarAviso(avisoDeQueda(nomeNoLugar(sala, eu, s, assistindo), bot)); continue; }
      const resta = Math.max(1, Math.ceil((ate - Date.now() - e.desvio) / 1000)), id = ++contador;
      setToast({ id, texto: `${nomeNoLugar(sala, eu, s, assistindo)} caiu. Se não voltar em ${resta}s, um bot assume o lugar.` });
      setTimeout(() => setToast((t) => (t?.id === id ? null : t)), 3200);
    }
  }, [quedas]);

  // A vaga abriu: o lugar de alguém passou para o bot (não voltou em 45 s ou saiu pelo botão). Avisa os 6 uma vez.
  const lugaresAntes = useRef<SalaPublica['lugares'] | null>(null);
  useEffect(() => {
    const antes = lugaresAntes.current;
    lugaresAntes.current = sala?.lugares ?? null;
    if (!antes || !sala || eu === null || sala.fase !== 'jogo') return;
    sala.lugares.forEach((l, s) => {
      const a = antes[s];
      if ((s === eu && !assistindo) || l?.tipo !== 'pessoa' || !ehVaga(l) || a?.tipo !== 'pessoa' || a.id !== l.id || ehVaga(a)) return;
      mostrarAviso(avisoDeVaga(l.perfil.apelido, l.botApelido ?? 'bot', a.conectado));
    });
  }, [sala]);

  // Cartas sendo dadas. O servidor manda o prazo da distribuição, também para quem chega no meio
  // (quem chega atrasado vê a animação mais rápida, ou nenhuma se faltar pouco).
  const prazoDist = e.prazos.find((p) => p.tipo === 'distribuicao');
  const chaveDist = prazoDist && v?.hand && eu !== null ? `${e.partida}:${v.handNo}` : null;
  useEffect(() => {
    const est = estadoRef.current, pz = est.prazos.find((p) => p.tipo === 'distribuicao'), vv = est.visao, eu0 = est.eu;
    if (!chaveDist || !pz || !vv || eu0 === null || distribuidas.current.has(chaveDist)) return;
    distribuidas.current.add(chaveDist);
    const resta = pz.ate - agoraNoServidor(est);
    if (movimentoReduzido() || resta < 1200) return;
    const k = Math.min(1, (resta - 150) / DURACAO_DISTRIBUICAO), dealer = vv.dealer, meus: ReturnType<typeof setTimeout>[] = [];
    const ag = (fn: () => void, ms: number) => { meus.push(setTimeout(fn, ms * k)); };
    setDist({ chave: chaveDist, dealer, fase: 'embaralhar', dadas: [0, 0, 0, 0, 0, 0] });
    som.embaralhar();
    ag(() => setDist((d) => (d?.chave === chaveDist ? { ...d, fase: 'dando' } : d)), DISTRIBUIR.embaralhar);
    let alvo = next(dealer);
    for (let i = 0; i < 18; i++) {
      const s = alvo;
      ag(() => {
        const somar = () => setDist((d) => (d?.chave === chaveDist ? { ...d, dadas: d.dadas.map((n, j) => (j === s ? n + 1 : n)) } : d));
        const caixa = caixaRef.current;
        som.distribuir();
        if (!caixa) { somar(); return; }
        const r = (s - eu0 + 6) % 6, m = monteDe((dealer - eu0 + 6) % 6);
        const para = r === 0 ? alvoDaMao() : lugares(geoRef.current)[r]!;
        const sl = estadoRef.current.sala;
        voarCarta(caixa, imagemDaCarta('verso', sl?.liga ? versoDaLiga(sl.liga) : versoDoCosmetico(sl?.visual?.verso)), [m[0] + 25, m[1] + 35], para, r === 0 ? 1.6 : 0.8, somar);
      }, DISTRIBUIR.embaralhar + i * DISTRIBUIR.passo);
      alvo = next(alvo);
    }
    ag(() => {
      setDist((d) => (d?.chave === chaveDist ? null : d));
      setPouso(dealer);
      agendar(() => setPouso(null), 1100);
    }, DURACAO_DISTRIBUICAO);
    return () => {
      for (const t of meus) clearTimeout(t);
      distribuidas.current.delete(chaveDist);
      setDist((d) => (d?.chave === chaveDist ? null : d));
    };
  }, [chaveDist]);

  // Gritos gravados de quem está sentado: baixa e decodifica uma vez, para tocar sem atraso no pedido.
  const chavesGritos = (sala?.lugares ?? []).flatMap((l) => (l?.tipo === 'pessoa' && l.perfil.gritos
    ? Object.entries(l.perfil.gritos).map(([g, ver]) => `${l.id}:${g}:${ver}`) : [])).join(',');
  useEffect(() => {
    for (const k of chavesGritos.split(',').filter(Boolean)) {
      if (gravacoes.current.has(k)) continue;
      gravacoes.current.set(k, null);
      const [id, g, ver] = k.split(':') as [string, Grito, string];
      const url = conta.urlGrito(id, g, ver);
      if (!url) continue;
      void fetch(url).then((r) => (r.ok ? r.arrayBuffer() : Promise.reject(new Error(String(r.status)))))
        .then(decodificar).then((buf) => { if (buf) gravacoes.current.set(k, buf); }).catch(() => {});
    }
  }, [chavesGritos]);

  // O servidor recusa jogada e truco enquanto espera as cartas chegarem (distribuição, LEVOU na mesa, entrada da
  // liga). A vez só começa quando esse prazo sai do estado: a animação daqui acaba um pouco antes, e com movimento
  // reduzido ou chegando no meio da distribuição nem acontece.
  const esperaDoServidor = e.prazos.some((p) => p.tipo === 'distribuicao' || p.tipo === 'rodada' || p.tipo === 'entrada');
  // Sua vez: um sinal curto (e vibração no celular); nos últimos 5 s, um tique por segundo.
  const hh = v?.hand;
  // Sentou na vaga e espera a próxima mão: o bot ainda joga no seu lugar, e nada é seu para jogar.
  const naEspera = !assistindo && eu !== null && esperaAMao(sala?.lugares[eu]);
  const chaveDaVez = hh && eu !== null && !assistindo && !naEspera && v!.phase === 'play' && hh.turn === eu && !hh.ended && pausa === null && !dist && !esperaDoServidor
    ? `${v!.handNo}:${hh.rounds.length}:${hh.rounds[hh.rounds.length - 1]?.plays.length}` : null;
  useEffect(() => { if (chaveDaVez) { som.suaVez(); vibrar(40); } }, [chaveDaVez]);
  const pzVez = chaveDaVez ? e.prazos.find((p) => p.tipo === 'vez' && p.lugar === eu) : undefined;
  const restaVez = pzVez ? Math.ceil((pzVez.ate - agora) / 1000) : null;
  useEffect(() => { if (restaVez !== null && restaVez > 0 && restaVez <= 5) som.tique(); }, [restaVez]);
  const pref = usePreferenciasSom();

  // Atalhos: 1, 2, 3 escolhem e jogam a carta; T pede truco.
  const atalhos = useRef<(k: string) => void>(() => {});
  useEffect(() => {
    const f = (ev: KeyboardEvent) => {
      if (ev.target instanceof HTMLInputElement || ev.ctrlKey || ev.metaKey || ev.altKey) return;
      atalhos.current(ev.key.toLowerCase());
    };
    window.addEventListener('keydown', f);
    return () => window.removeEventListener('keydown', f);
  }, []);

  // O cartão do jogador fecha sozinho na sua vez e numa decisão de truco ou mão de onze do seu trio.
  const suaVezAgora = !!h && v?.phase === 'play' && h.turn === eu && !h.ended;
  const decisaoDoTrio = eu !== null && !!e.votacao && e.votacao.team === team(eu) && e.votacao.stage !== 'done';
  const chegou = useRef({ vez: suaVezAgora, decisao: decisaoDoTrio });
  useEffect(() => {
    const antes = chegou.current;
    chegou.current = { vez: suaVezAgora, decisao: decisaoDoTrio };
    if ((suaVezAgora && !antes.vez) || (decisaoDoTrio && !antes.decisao)) setFolha((f) => (f === 'jogador' ? null : f));
  }, [suaVezAgora, decisaoDoTrio]);
  const abrirJogador = (s: Seat) => { if (assistindo) return; setVerJogador(s); setFolha('jogador'); };

  if (!v || !sala || eu === null) return null;

  const meu = team(eu), deles = (1 - meu) as Team;
  // A plateia: o "👀 3" para quem joga (com a lista ao tocar) e o número para quem assiste.
  const naPlateia = sala.plateia ?? [], quantosAssistem = assistindo ? plateia!.quantos : naPlateia.length;
  const souDonoDaSala = !assistindo && !sala.liga && !sala.campeonato && e.usuario === sala.dono; // o dono tira alguém da plateia
  const esperaReacao = Math.max(0, Math.ceil((reacaoAte - Date.now()) / 1000));
  const rel = (s: Seat) => (s - eu + 6) % 6;
  const seatDeRel = (r: number) => ((eu + r) % 6) as Seat;
  const nome = (s: Seat) => nomeNoLugar(sala, eu, s, assistindo);
  const LUG = lugares(geo);
  const prazo = (tipo: string, f: (p: { lugar?: Seat; trio?: Team }) => boolean) => e.prazos.find((p) => p.tipo === tipo && f(p)) ?? null;
  const vezMs = sala.regras.tempoPorJogadaMs;

  const minhaVez = !assistindo && !!h && !naEspera && v.phase === 'play' && h.turn === eu && !h.ended && pausa === null && !dist && !esperaDoServidor;
  // Votação do seu trio (truco ou mão de onze): a doca fica acima de Sinais e Reações e a mão sobe.
  const emVotacao = !!e.votacao && e.votacao.team === meu && e.votacao.stage !== 'done' && !!e.votacao.votes;
  const rodadaAtual = h?.rounds[h.rounds.length - 1];
  // Esconder: na 1ª rodada e na rodada depois de empachar, todo mundo joga aberto (packages/regras, canHide).
  const podeEsconder = minhaVez && !!h && podeEsconderNaRodada(h.rounds);
  const porQueAberta = minhaVez && h && !podeEsconder ? (h.rounds.length <= 1 ? 'Na 1ª rodada todo mundo joga aberto' : 'Rodada empachada: carta aberta') : null;
  const avisar = (texto: string) => {
    const id = Date.now();
    setToast({ id, texto });
    setTimeout(() => setToast((t) => (t?.id === id ? null : t)), 2200);
  };
  const proximo = h ? nextLevel(h.value) : null;
  const podeTruco = minhaVez && !!h && !h.special && !h.pending && proximo !== null && (h.canRaiseTeam === null || h.canRaiseTeam === meu) && (!folha || pc);
  const sinaisAtivos = !assistindo && !!h && !naEspera && !h.ended && sala.regras.sinais !== 'desligados' && (v.phase === 'play' || v.phase === 'respond' || v.phase === 'onze');

  function jogar(i: number) {
    if (!h || !minhaVez) return;
    const coberta = esconder && podeEsconder;
    origemMinha.current = centroNaMao(quantas, i, geo);
    const carta = h.myCards[i];
    if (carta) c.enviar({ t: 'jogar', carta, coberta });
    setSel(null); setEsconder(false);
  }
  function tocar(i: number) {
    if (sel === i && minhaVez) jogar(i); else setSel(sel === i ? null : i);
  }
  function truco() { if (podeTruco) c.enviar({ t: 'truco' }); }
  atalhos.current = (k) => {
    if (k === 't') truco();
    const n = Number(k);
    if (n >= 1 && n <= 3) tocar(n - 1);
  };

  // ---- placar ----
  let valor: string, quente = false;
  if (h?.special === 'onze') { valor = 'Mão de onze'; quente = true; }
  else if (h?.special === 'ferro') { valor = 'Mão de ferro'; quente = true; }
  else if (h?.pending) { valor = 'Valendo ' + h.pending.level + '?'; quente = true; }
  else valor = 'Mão vale ' + (h?.value ?? 1);
  // Placar: os nomes de cada trio um embaixo do outro, sem vírgula (você primeiro, no seu trio).
  const nomesDoTrio = (t: Team) => [0, 1, 2, 3, 4, 5].filter((s) => team(s as Seat) === t).sort((a, b) => (assistindo ? 0 : a === eu ? -1 : b === eu ? 1 : 0)).map((s) => nome(s as Seat));
  const listaDeNomes = (t: Team) => <span className="nomes" aria-label={nomesDoTrio(t).join(', ')}>{nomesDoTrio(t).map((n, i) => <span key={i} title={n}>{n}</span>)}</span>;

  // ---- centro da mesa ----
  const focoOnze = !assistindo && v.phase === 'onze' && h?.onzeTeam === meu && !dist;
  const rodada = h ? (pausa !== null ? h.rounds[pausa] : rodadaAtual) : undefined;
  // GANHANDO ou EMPACHADO na rodada em andamento; no fim da rodada, LEVOU ou EMPACHOU (packages/regras/src/rodada.ts).
  const estado = rodada && h ? estadoDaRodada(rodada, h.manilha) : null;
  const naRodada = pausa === null && (rodada?.plays.length ?? 0) > 1 ? estado : null;
  // Manilha dourada: toda carta aberta com o valor da manilha desta mão. A vira e a carta coberta nunca ficam douradas.
  const ehManilha = (carta: string | undefined) => ({ manilha: !!h && !!carta && carta !== 'verso' && rankOf(carta as Card) === h.manilha });

  const monteDeQuemDeu = (s: Seat) => monteDe(rel(s));
  const centro = !h ? null : dist ? (
    <div className={'pile' + (dist.fase === 'embaralhar' ? ' shuffle' : '')} aria-hidden="true"
      style={{ left: monteDeQuemDeu(dist.dealer)[0], top: monteDeQuemDeu(dist.dealer)[1] }}>
      <img src={imagemDaCarta('verso', verso)} alt="" style={{ left: 4, top: 5 }} />
      <img src={imagemDaCarta('verso', verso)} alt="" style={{ left: 2, top: 2.5 }} />
      <img src={imagemDaCarta('verso', verso)} alt="" style={{ left: 0, top: 0 }} />
      <img src={imagemDaCarta('verso', verso)} alt="" style={{ left: -1, top: -1.5 }} />
    </div>
  ) : focoOnze ? (
    <>
      <span className="tag gold" style={{ left: 195, top: 326, translate: '-50% 0', zIndex: 3 }}>VIRA</span>
      <img className="card" src={imagemDaCarta(h.vira)} alt={'Vira: ' + nomeDoId(h.vira)} style={{ left: 160, top: 336, width: 70, height: 98, borderRadius: 9 }} />
      <span className="abs manilha" style={{ top: 442, left: 140, width: 110 }}>Manilha: {h.manilha}</span>
      {[2, 4].map((r) => {
        const s = seatDeRel(r), o = ONZE[r]!, cartas = h.partnerCards[s] ?? [];
        return (
          <div key={r}>
            {cartas.map((carta, i) => (
              <img key={carta} className="card" src={imagemDaCarta(carta, verso, ehManilha(carta))} alt={nomeDoId(carta, ehManilha(carta)) + ', da mão de ' + nome(s)}
                style={{ width: 56, height: 78, borderRadius: 7, left: o[0] + i * 20, top: o[1] + Math.abs(i - 1) * 4, transform: `rotate(${(i - 1) * 7}deg)` }} />
            ))}
            <span className="ptag" style={{ left: o[0] + 48, top: o[1] - 20, translate: '-50% 0' }}>{nome(s).toUpperCase()}</span>
          </div>
        );
      })}
    </>
  ) : (
    <>
      <div className={'pile' + (pouso !== null ? ' pileIn' : '')}
        style={{ left: MONTE[0], top: MONTE[1], ...(pouso !== null ? varsCss({ '--px': monteDeQuemDeu(pouso)[0] - MONTE[0] + 'px', '--py': monteDeQuemDeu(pouso)[1] - MONTE[1] + 'px' }) : {}) }}>
        <img src={imagemDaCarta('verso', verso)} alt="" style={{ left: 4, top: 5 }} />
        <img src={imagemDaCarta('verso', verso)} alt="" style={{ left: 2, top: 2.5 }} />
        <img className={'vira' + (pouso !== null ? ' viraIn' : '')} src={imagemDaCarta(h.vira)} alt={'Vira: ' + nomeDoId(h.vira)} />
        <img src={imagemDaCarta('verso', verso)} alt="" style={{ left: 0, top: 0 }} />
        <img src={imagemDaCarta('verso', verso)} alt="" style={{ left: -1, top: -1.5 }} />
      </div>
      <span className="abs manilha">Manilha: {h.manilha}</span>
      {minhaVez && <div className="abs slot" aria-hidden="true" />}
      {rodada?.plays.map((p) => {
        const r = rel(p.seat), pos = JOGADA[r]!, rot = ROTACAO[r]!;
        const chave = chaveDoVoo(v.handNo, pausa ?? h.rounds.length - 1, p.seat), voo = voos[chave];
        const indo = recolher !== null && recolher.rodada === pausa;
        let estilo: React.CSSProperties = { left: pos[0], top: pos[1], transform: `rotate(${rot}deg)`, ...varsCss({ '--rot': rot + 'deg' }) };
        if (voo && !indo) estilo = { ...estilo, ...varsCss({ '--fx': voo.fx + 'px', '--fy': voo.fy + 'px', '--fs': String(voo.fs), '--fo': String(voo.fo) }) };
        if (indo) {
          const para = recolher.para === null ? [MONTE[0] + 25, MONTE[1] + 35] : LUG[rel(recolher.para)]!;
          estilo.transform = `translate(${para[0] - pos[0] - 26}px,${para[1] - pos[1] - 36}px) scale(.45) rotate(${rot}deg)`;
        }
        const ganhando = pausa !== null ? rodada.result !== 'tie' && p.seat === rodada.winnerSeat : naRodada?.ganhando === p.seat;
        const empatou = pausa !== null && rodada.result === 'tie' && !!estado?.empachados.includes(p.seat);
        const empachado = !!naRodada?.empachados.includes(p.seat);
        const tagEm = { left: pos[0] + 26, top: pos[1] - 9, translate: '-50% 0', zIndex: 3 };
        const id = p.hidden && p.seat !== eu ? 'verso' : p.card ?? 'verso';
        const dourada = p.hidden ? {} : ehManilha(id);
        return (
          <div key={chave}>
            <img className={'card t' + (ganhando && !indo ? ' win' : '') + (empachado && !indo ? ' empachado' : '') + (voo && !indo ? ' enter' : '') + (indo ? ' recolhe' : '')}
              src={imagemDaCarta(p.hidden ? 'verso' : id, verso, dourada)}
              alt={(p.hidden ? 'Carta coberta' : nomeDoId(id, dourada)) + ', de ' + nome(p.seat)} style={estilo} />
            {indo ? null : p.hidden ? <span className="tag dark" style={tagEm}>COBERTA</span>
              : empatou ? <span className="tag dark" style={tagEm}>EMPACHOU</span>
                : empachado ? <span className="tag dark empachado" style={tagEm}>EMPACHADO</span>
                  : ganhando ? <span className="tag gold" style={tagEm}>{pausa !== null ? 'LEVOU' : 'GANHANDO'}</span> : null}
          </div>
        );
      })}
    </>
  );

  // ---- lugares ----
  function anel(s: Seat): React.CSSProperties {
    if (!h || v!.phase !== 'play' || h.turn !== s || h.ended || pausa !== null || dist) return {};
    const p = prazo('vez', (x) => x.lugar === s);
    if (!p) return { background: 'conic-gradient(var(--gold-400) 0 100%)' };
    const resta = Math.max(0, p.ate - agora), frac = Math.min(1, resta / vezMs), cor = resta <= 5000 ? 'var(--eles-400)' : 'var(--gold-400)';
    return { background: `conic-gradient(${cor} 0 ${(frac * 100).toFixed(1)}%, rgba(255,255,255,.15) ${(frac * 100).toFixed(1)}% 100%)` };
  }
  function rotuloDoLugar(l: LugarPublico | null, s: Seat) {
    // Vaga aberta: o chip dourado; com alguém esperando a próxima mão, o nome de quem entra.
    if (esperaAMao(l)) return <span className="tag gold vaga longa" style={{ position: 'static' }}>{l!.perfil.apelido} entra na próxima mão</span>;
    if (ehVaga(l)) return <span className="tag gold vaga" style={{ position: 'static' }}>VAGA ABERTA</span>;
    if (l?.tipo === 'pessoa' && l.bot) return <span className="tag dark" style={{ position: 'static' }}>BOT</span>;
    if (l?.tipo === 'pessoa' && !l.conectado) {
      const p = prazo('reconexao', (x) => x.lugar === s);
      return <span className="tag dark" style={{ position: 'static' }}>Reconectando{p ? ' · ' + Math.ceil(Math.max(0, p.ate - agora) / 1000) + 's' : ''}</span>;
    }
    return null;
  }
  // Selo PÉ em quem fecha a rodada mostrada (o último a jogar): na 1ª, quem deu as cartas; na 2ª e na 3ª, quem joga antes
  // de quem abre. No LEVOU ou EMPACHOU fica em quem fechou; muda quando a rodada seguinte começa. Fica fora do anel, para
  // não esmaecer no foco da mão de onze.
  const pe = rodada ? quemFecha(rodada.leader) : v.dealer;
  const seloDoPe = (s: Seat) => h && pe === s
    ? <span className="selo-pe" role="img" aria-label={(s === eu ? 'Você' : nome(s)) + ' é o pé'}>PÉ</span>
    : null;
  // Quem assiste vê o lugar de baixo como os outros: rosto e nome, sem a mão.
  const assentos = (assistindo ? [0, 1, 2, 3, 4, 5] : [1, 2, 3, 4, 5]).map((r) => {
    const s = seatDeRel(r), l = sala.lugares[s] ?? null, cor = corDoTrio(team(s), meu, assistindo), c0 = LUG[r]!;
    const n = dist ? dist.dadas[s]! : h?.cardCounts[s] ?? 0;
    const dim = focoOnze && team(s) !== meu ? { opacity: 0.4 } : {};
    const fora = l?.tipo === 'pessoa' && !l.conectado && !l.bot ? { opacity: 0.55 } : {};
    const vaga = ehVaga(l) || esperaAMao(l), r0 = rostoDoLugar(l);
    const rosto = (
      <button className="rosto-lugar" aria-label={'Ver ' + nome(s) + (ehVaga(l) ? ', vaga aberta' : esperaAMao(l) ? ', ' + l!.perfil.apelido + ' entra na próxima mão' : '')} onClick={() => abrirJogador(s)}>
        <div className={'ring' + (vaga ? ' vaga' : '')} style={{ ...anel(s), ...dim, ...fora }}>
          <Rosto perfil={r0.perfil} usuario={r0.usuario} bot={r0.bot} cor={cor} moldura={l?.tipo === 'pessoa' && !vaga ? l.moldura : undefined}
            coroa={l?.tipo === 'pessoa' && !vaga && !!l.coroa} />
          <NivelNoRosto l={l} />
        </div>
        {seloDoPe(s)}
      </button>
    );
    const costas = <div className="backs" aria-label={n + ' cartas'}>{Array.from({ length: n }, (_, i) => <img key={i} src={imagemDaCarta('verso', verso)} alt="" />)}</div>;
    if (r === 0) {
      return (
        <div key={r} className="seat meu-lugar" style={{ left: c0[0] - 27, top: c0[1] - 27 }}>
          {rosto}<span className="nm">{nome(s)}</span>{rotuloDoLugar(l, s)}
        </div>
      );
    }
    if (r === 3) {
      return (
        <div key={r}>
          <div className="abs" style={{ left: c0[0] - 27, top: c0[1] - 27 }}>{rosto}</div>
          <span className="abs nm" style={{ left: 226, top: 128 }}>{nome(s)}</span>
          <div className="abs" style={{ left: 228, top: 153, display: 'flex', gap: 4, alignItems: 'center' }}>{costas}</div>
          {/* O chip do lugar de cima fica à esquerda do rosto, para caber na tela. */}
          <div className="abs rotulo-topo" style={{ top: c0[1] - 27 }}>{rotuloDoLugar(l, s)}</div>
        </div>
      );
    }
    return (
      <div key={r} className="seat" style={{ left: c0[0] - 42, top: c0[1] - 27 }}>
        {rosto}<span className="nm">{nome(s)}</span>{costas}{rotuloDoLugar(l, s)}
      </div>
    );
  });
  const eu0 = sala.lugares[eu];
  // Rosto e nome no seu lado da mesa, com o nome à direita (canvas F06); deitado também, para o nome não sair da tela.
  const meuAssento = assistindo ? null : (
    <div className="seat meu-lugar" style={{ left: LUG[0]![0] - 27, top: LUG[0]![1] - 27 }}>
      <button className="rosto-lugar" aria-label="Ver você" onClick={() => abrirJogador(eu)}>
        <div className="ring" style={anel(eu)}>
          <Rosto perfil={eu0?.perfil} usuario={eu0?.tipo === 'pessoa' ? eu0.id : undefined} cor="var(--nos-400)" moldura={eu0?.tipo === 'pessoa' ? eu0.moldura : undefined}
            coroa={eu0?.tipo === 'pessoa' && !!eu0.coroa} />
          <NivelNoRosto l={eu0 ?? null} />
          {subida && <span key={subida.id} className="rodagem-sobe" aria-hidden="true">+{subida.quanto}</span>}
        </div>
        {seloDoPe(eu)}
      </button>
      <span className="nm">Você</span>
      {subida && <span className="so-leitor" role="status">{nomeDoLance(subida.lance)}: mais {subida.quanto} de Rodagem</span>}
    </div>
  );

  // ---- mão ----
  const minhaVezPrazo = minhaVez ? prazo('vez', (x) => x.lugar === eu) : null;
  const meusSegundos = minhaVezPrazo ? Math.ceil(Math.max(0, minhaVezPrazo.ate - agora) / 1000) : null;
  const total = h?.myCards.length ?? 0;
  const quantas = dist ? Math.min(dist.dadas[eu]!, total) : total;
  const mao = Array.from({ length: quantas }, (_, i) => {
    const id = h!.myCards[i]!;
    const p = posicaoNaMao(quantas, i, geo), escolhida = sel === i;
    const manilha = id && rankOf(id) === h!.manilha;
    return (
      <button key={id ?? 'cega' + i} className={'hcard' + (escolhida ? ' sel' : '') + (escolhida && esconder ? ' hid' : '')}
        style={{ left: p[0], top: p[1] - (emVotacao ? (pc ? MAO_SOBE_NO_COMPUTADOR : !paisagem ? MAO_SOBE_NA_VOTACAO : 0) : 0), transform: `rotate(${p[2]}deg)` + (escolhida ? ' translateY(-16px)' : '') }}
        aria-label={nomeDoId(id, ehManilha(id)) + (escolhida ? ', selecionada' : '')}
        onClick={() => tocar(i)}>
        <img src={imagemDaCarta(id ?? 'verso', verso, ehManilha(id))} alt="" />
        {manilha && <span className="mtag">{NAIPE_DA_MANILHA[suitOf(id!)]}</span>}
      </button>
    );
  });
  const dica = minhaVez ? (
    <div className="abs hint">
      <span>
        {sel !== null ? (esconder && podeEsconder ? 'Toque: coberta' : 'Toque de novo') : 'Sua vez'}
        {meusSegundos !== null && <> · <b style={{ color: meusSegundos <= 5 ? 'var(--eles-400)' : 'var(--gold-400)' }}>{meusSegundos}s</b></>}
      </span>
    </div>
  ) : null;

  // ---- botão de truco ----
  let rotulo: string, pequeno: string;
  if (h?.special === 'onze') { rotulo = 'MÃO DE ONZE'; pequeno = 'sem truco'; }
  else if (h?.special === 'ferro') { rotulo = 'MÃO DE FERRO'; pequeno = 'sem truco'; }
  else if (!proximo) { rotulo = 'VALE 12'; pequeno = 'no máximo'; }
  else if (h?.canRaiseTeam === deles) { rotulo = PEDIDO[proximo]!; pequeno = 'só Eles pedem'; }
  else { rotulo = PEDIDO[proximo]!; pequeno = 'vale ' + proximo; }

  // ---- votação do trio ----
  const vot = e.votacao;
  const nossaVotacao = vot && vot.team === meu && vot.stage !== 'done' && vot.votes ? vot : null;
  const deles_decidindo = vot && vot.team !== meu && vot.stage !== 'done';
  let doca: React.ReactNode = null;
  if (nossaVotacao && h) {
    const isT = nossaVotacao.kind === 'truco', L = nossaVotacao.level, nl = isT ? nextLevel(L as 3) : null;
    const pz = prazo(nossaVotacao.stage === 'confirm' ? 'confirmacao' : 'voto', (x) => x.trio === meu);
    const seg = pz ? Math.ceil(Math.max(0, pz.ate - agora) / 1000) : null;
    const trio = [0, 1, 2, 3, 4, 5].filter((s) => team(s as Seat) === meu) as Seat[];
    // Só voto de pessoa decide: o bot do trio com pessoa aparece com a opinião, tracejado, fora da conta.
    const decidem = (nossaVotacao.contam ?? trio) as Seat[];
    const votos = (
      <div className="votes">
        {trio.map((s) => {
          const opina = !decidem.includes(s), opiniao = nossaVotacao.opinioes?.[s];
          const marca = opina ? (opiniao ? VOTO_EMOJI[opiniao] : '…')
            : nossaVotacao.stage === 'vote' ? (nossaVotacao.votes![s] ? VOTO_EMOJI[nossaVotacao.votes![s]!] : '⏳')
              : s === nossaVotacao.confirmer ? (s === eu ? VOTO_EMOJI[nossaVotacao.votes![eu] ?? 'accept'] : '⏳') : '🏃';
          const l = sala.lugares[s];
          return (
            <span key={s} className={'vote' + (opina ? ' opiniao' : marca !== '⏳' ? ' done' : '')}
              title={opina ? `${nome(s)} acha ${opiniao ? VOTO_EMOJI[opiniao] : '…'} (não conta)` : nome(s)}>
              <Rosto perfil={l?.perfil} usuario={l?.tipo === 'pessoa' ? l.id : undefined} bot={ehBot(l)} cor="transparent" tamanho={28} estilo={{ border: 0 }} />
              <i>{marca}</i>
            </span>
          );
        })}
      </div>
    );
    const segundos = seg !== null ? <span className="sec" style={{ color: seg <= 5 ? 'var(--eles-400)' : 'var(--gold-400)' }}>{seg}s</span> : null;
    if (nossaVotacao.stage === 'vote') {
      const meuVoto = nossaVotacao.votes![eu];
      const pick = (x: string) => (meuVoto === x ? ' picked' : '');
      const titulo = isT ? PEDIDO[L] + '! de ' + (h.pending ? nome(h.pending.bySeat) : '') : 'MÃO DE ONZE';
      const outra = decidem.find((s) => s !== eu);
      const quemDecide = decidem.length === 1 ? 'Você decide.' : decidem.length === 2 ? `Você e ${nome(outra!)} decidem.` : 'Maioria decide.';
      doca = (
        <div className="dock" role="dialog" aria-label={titulo}>
          <div className="top">
            <div className="ttl"><b style={{ color: isT ? 'var(--eles-400)' : 'var(--gold-400)' }}>{titulo}</b><span>{isT ? `${quemDecide} Sem voto, corre.` : decidem.length === 3 ? 'Vale 3. Sem voto, corre.' : `Vale 3. ${quemDecide} Sem voto, corre.`}</span></div>
            {votos}{segundos}
          </div>
          <div className="btns">
            <button className={'btn2' + pick('run')} style={{ flex: 1, padding: '0 8px' }} onClick={() => c.enviar({ t: 'votar', opcao: 'run' })}>
              Correr{isT && <small>+{h.value} para Eles</small>}
            </button>
            <button className={'btn1' + pick('accept')} style={{ flex: 1.3 }} onClick={() => c.enviar({ t: 'votar', opcao: 'accept' })}>
              {isT ? 'ACEITAR · ' + L : 'JOGAR · vale 3'}
            </button>
            {nl && <button className={'btn3' + pick('raise')} style={{ flex: 0.9 }} onClick={() => c.enviar({ t: 'votar', opcao: 'raise' })}>{PEDIDO[nl]}!</button>}
          </div>
        </div>
      );
    } else if (nossaVotacao.confirmer === eu) {
      // Só quem votou para jogar contra a maioria recebe a confirmação (canvas F07b). Quem votou no próximo valor
      // confirma já pedindo esse valor.
      const pedi = isT && !!nl && nossaVotacao.votes![eu] === 'raise';
      const correram = decidem.filter((s) => s !== eu);
      const outros = correram.map(nome).join(' e ') + (correram.length === 1 ? ' votou correr' : ' votaram correr');
      doca = (
        <div className="dock confirm" role="alertdialog" aria-label="Você decide">
          <div className="top">
            <div className="ttl"><b style={{ color: 'var(--gold-400)' }}>VOCÊ DECIDE</b><span>{outros}</span></div>
            {votos}{segundos}
          </div>
          <div className="btns-confirmar">
            <button className="btn2" onClick={() => c.enviar({ t: 'confirmar', opcao: 'run' })}>Correr junto</button>
            <button className="btn1" onClick={() => c.enviar({ t: 'confirmar', opcao: 'insist' })}>
              {!isT ? 'JOGAR MESMO ASSIM' : pedi ? `PEDIR ${PEDIDO[nl!]} MESMO ASSIM` : 'ACEITAR MESMO ASSIM'}
            </button>
          </div>
        </div>
      );
    } else {
      // Os parceiros só veem quem está decidindo, com o tempo (canvas F07c).
      const quem = nossaVotacao.confirmer != null ? nome(nossaVotacao.confirmer as Seat) : 'Alguém';
      doca = (
        <div className="dock decidindo" role="status" aria-label={quem + ' está decidindo'}>
          <div className="top">
            <div className="ttl"><b style={{ color: 'var(--gold-400)' }}>SÓ FALTA {quem.toUpperCase()}</b><span>{quem} está decidindo</span></div>
            {votos}{segundos}
          </div>
          <div className="aguardando">
            {!isT ? `Se ${quem} jogar, o trio joga valendo 3`
              : nl && nossaVotacao.confirmer != null && nossaVotacao.votes![nossaVotacao.confirmer] === 'raise' ? `Se ${quem} confirmar, o trio aceita e pede ${PEDIDO[nl]}`
                : `Se ${quem} aceitar, o trio joga valendo ${L}`}
          </div>
        </div>
      );
    }
  }

  // ---- folhas: sinais, reações e regras ----
  const esperaFrase = Math.max(0, Math.ceil((fraseAte - Date.now()) / 1000));
  const [pa, pb] = partners(eu);
  let conteudoFolha: React.ReactNode = null;
  // Sinal: um toque no gesto envia para os dois parceiros. No celular, a folha fecha; o gesto fica marcado e os botões
  // travam por 2 s.
  const passarSinal = (gesto: Gesture) => {
    if (sinalEnviado) return;
    c.enviar({ t: 'sinal', gesto });
    setSinalEnviado(gesto);
    agendar(() => setSinalEnviado(null), TRAVA_DO_SINAL_MS);
    setFolha(null);
  };
  const rostoDoParceiro = (s: Seat) => {
    const l = sala.lugares[s];
    return <Rosto key={s} perfil={l?.perfil} usuario={l?.tipo === 'pessoa' ? l.id : undefined} bot={ehBot(l)} cor="var(--nos-400)" tamanho={28} />;
  };
  const painelSinais = (
    <>
      <div className="vai-para">
        <span className="rostos" aria-hidden="true">{rostoDoParceiro(pa!)}{rostoDoParceiro(pb!)}</span>
        <span>Vai para {nome(pa!)} e {nome(pb!)}</span>
      </div>
      <div className="grid-sinais">
        {SINAIS.map((x) => (
          <button key={x.gesture} className={'gest' + (sinalEnviado === x.gesture ? ' sel' : '')} aria-pressed={sinalEnviado === x.gesture}
            disabled={sinalEnviado !== null || !sinaisAtivos} aria-label={x.gesto + ': ' + x.carta} onClick={() => passarSinal(x.gesture)}>
            <span className="gemo" aria-hidden="true">{x.emoji}</span>
            <span className="carta-sinal">{x.carta}{x.naipe && <span className={x.naipe === 'h' || x.naipe === 'd' ? 'red' : ''}> {SIMBOLO[x.naipe]}</span>}</span>
          </button>
        ))}
      </div>
      <p>{sala.regras.sinais === 'todos' ? 'Nesta sala, todos veem o gesto.' : 'Os adversários veem só que você passou sinal, por 2 s. O gesto fica com o seu trio.'}</p>
    </>
  );
  // Frases prontas em duas abas, Frases e Zoeira 😜. Sem a zoeira (a chave da pessoa ou a regra da sala), só Frases,
  // sem as abas. As duas abas dividem os 5 s.
  const zoeiraLigada = sala.regras.zoeira !== false && e.zoeira;
  const aba: AbaFrases = zoeiraLigada ? abaFrases : 'frases';
  const escolherAba = (a: AbaFrases) => {
    setAbaFrases(a);
    try { localStorage.setItem(CHAVE_ABA_FRASES, a); } catch { /* sem armazenamento: a escolha vale só agora */ }
  };
  const proxima = esperaFrase > 0 && <span> · próxima em {esperaFrase} s</span>;
  const painelReacoes = (
    <>
      <div className="grid4">
        {REACOES.map((r) => <button key={r} className="btn2 react" aria-label={'Reagir ' + r} onClick={() => { c.enviar({ t: 'reacao', emoji: r }); setFolha(null); }}>{r}</button>)}
      </div>
      {zoeiraLigada ? (
        <div className="abas-frases" role="tablist" aria-label="Frases prontas">
          {(['frases', 'zoeira'] as const).map((a) => (
            <button key={a} role="tab" aria-selected={aba === a} className={aba === a ? 'on' : undefined} onClick={() => escolherAba(a)}>
              <span>{a === 'frases' ? 'Frases' : 'Zoeira 😜'}</span>{esperaFrase > 0 && <small>próxima em {esperaFrase} s</small>}
            </button>
          ))}
        </div>
      ) : <h3 className="rotulo-folha">FRASES{proxima}</h3>}
      <div className="frases" {...(zoeiraLigada ? { role: 'tabpanel', 'aria-label': aba === 'zoeira' ? 'Zoeira' : 'Frases' } : {})}>
        {(aba === 'zoeira' ? ZOEIRA : FRASES).map((f) => (
          <button key={f} className="btn2 frase" disabled={esperaFrase > 0} onClick={() => {
            c.enviar({ t: 'frase', frase: f });
            setFraseAte(Date.now() + INTERVALO_FRASE_MS);
            setFolha(null);
          }}>{f}</button>
        ))}
      </div>
      <p className="nota-folha">Aparece sobre o seu rosto por 3 s. Uma frase a cada 5 s.{zoeiraLigada && ' Quem não curte desliga a zoeira em Configurações.'}</p>
    </>
  );
  // Reações de quem assiste: na faixa Plateia, uma a cada 5 s.
  const painelReacoesPlateia = plateia ? (
    <>
      <div className="grid4">
        {REACOES.map((r) => (
          <button key={r} className="btn2 react" aria-label={'Reagir ' + r} disabled={esperaReacao > 0} onClick={() => {
            if (plateia.reagir(r)) setReacaoAte(Date.now() + INTERVALO_REACAO_PLATEIA_MS);
            setFolha(null);
          }}>{r}</button>
        ))}
      </div>
      <p className="nota-folha">{esperaReacao > 0 ? `Próxima reação em ${esperaReacao} s.` : 'Uma reação a cada 5 s.'}</p>
    </>
  ) : null;
  const somDaMesa = (
    <>
      <div className="opt">
        <span>Sons e gritos<small>Cartas, pedidos de truco e momentos da partida.</small></span>
        <button className="sw" role="switch" aria-checked={pref.som} aria-label="Sons e gritos" onClick={() => mudarPreferencias({ som: !pref.som })} />
      </div>
      <div className="opt">
        <span>Vozes gravadas<small>Os gritos que cada jogador gravou. Desligado, todo mundo grita com a voz padrão.</small></span>
        <button className="sw" role="switch" aria-checked={pref.vozesGravadas} aria-label="Vozes gravadas" onClick={() => mudarPreferencias({ vozesGravadas: !pref.vozesGravadas })} />
      </div>
    </>
  );
  if (folha === 'sinais' && sinaisAtivos) {
    conteudoFolha = (
      <div className="sheet" role="dialog" aria-label="Sinais">
        <div className="cab-folha">
          <div><h2>Sinais</h2><span>Fixos: valem em toda mão</span></div>
          <button className="fechar-folha" aria-label="Fechar sinais" onClick={() => setFolha(null)}>×</button>
        </div>
        {painelSinais}
      </div>
    );
  } else if (folha === 'reacoes') {
    conteudoFolha = (
      <div className="sheet" role="dialog" aria-label="Reações">
        <div className="cab-folha">
          <div><h2>Reações</h2><span>Todo mundo na mesa vê, inclusive o trio adversário.</span></div>
          <button className="fechar-folha" aria-label="Fechar reações" onClick={() => setFolha(null)}>×</button>
        </div>
        {painelReacoes}
      </div>
    );
  } else if (folha === 'som') {
    conteudoFolha = (
      <div className="sheet" role="dialog" aria-label="Som da mesa">
        <h2>Som da mesa</h2>
        {somDaMesa}
        <button className="btn2" onClick={() => setFolha(null)}>Fechar</button>
      </div>
    );
  } else if (folha === 'menu') {
    // Menu da mesa no computador: o som e as regras, num painel na coluna da direita.
    conteudoFolha = (
      <div className="sheet" role="dialog" aria-label="Menu da mesa">
        <div className="cab-folha">
          <div><h2>Menu da mesa</h2></div>
          <button className="fechar-folha" aria-label="Fechar menu" onClick={() => setFolha(null)}>×</button>
        </div>
        <h3 className="rotulo-folha">SOM</h3>
        {somDaMesa}
        <h3 className="rotulo-folha">COMO JOGA ESTA MESA</h3>
        <ul className="rules">{REGRAS.map((r, i) => <li key={i} dangerouslySetInnerHTML={{ __html: r }} />)}</ul>
      </div>
    );
  } else if (folha === 'jogador' && verJogador !== null) {
    conteudoFolha = (
      <CartaoJogador lugar={sala.lugares[verJogador] ?? null} nome={nome(verJogador)} nos={team(verJogador) === meu} eu={verJogador === eu}
        aoFechar={() => setFolha(null)} aoChamar={naEspera ? undefined : aoChamarAmigos} />
    );
  } else if (folha === 'plateia') {
    conteudoFolha = (
      <div className="sheet" role="dialog" aria-label="Plateia">
        <div className="cab-folha">
          <div><h2>Plateia · {naPlateia.length}</h2><span>Quem assiste vê só o que está na mesa: nunca as cartas da mão, os sinais ou os votos.</span></div>
          <button className="fechar-folha" aria-label="Fechar plateia" onClick={() => setFolha(null)}>×</button>
        </div>
        <ul className="lista-plateia">
          {naPlateia.map((p) => (
            <li key={p.id}>
              <Rosto perfil={p.perfil} usuario={p.id} cor="var(--ink-300)" tamanho={36} />
              <span className="nome">{p.perfil.apelido}</span>
              {souDonoDaSala && <button className="btn2 tirar" onClick={() => c.enviar({ t: 'tirar_da_plateia', usuario: p.id })}>Tirar</button>}
            </li>
          ))}
        </ul>
        {souDonoDaSala && <p className="nota-folha">Quem você tirar não volta a assistir esta sala.</p>}
      </div>
    );
  } else if (folha === 'reacoes-plateia' && plateia) {
    conteudoFolha = (
      <div className="sheet" role="dialog" aria-label="Reações da plateia">
        <div className="cab-folha">
          <div><h2>Reagir</h2><span>Aparece na faixa Plateia, para a mesa e para quem assiste.</span></div>
          <button className="fechar-folha" aria-label="Fechar reações" onClick={() => setFolha(null)}>×</button>
        </div>
        {painelReacoesPlateia}
      </div>
    );
  } else if (folha === 'limite') {
    conteudoFolha = <FolhaLimite aoFechar={() => setFolha(null)} assinar={assinar} lerConvite={lerConvite} />;
  } else if (folha === 'regras') {
    conteudoFolha = (
      <div className="sheet" role="dialog" aria-label="Regras">
        <h2>Como joga esta mesa</h2>
        <ul className="rules">{REGRAS.map((r, i) => <li key={i} dangerouslySetInnerHTML={{ __html: r }} />)}</ul>
        <button className="btn2" onClick={() => setFolha(null)}>Fechar</button>
      </div>
    );
  }

  const dono = sala.lugares.find((l) => l?.tipo === 'pessoa' && l.id === sala.dono);
  const marca = sala.liga?.marca ?? null;
  // Liga patrocinada: a tela de entrada cobre a mesa até as cartas da primeira mão serem dadas.
  const prazoEntrada = marca ? e.prazos.find((p) => p.tipo === 'entrada') : undefined;
  // A tela final espera o LEVOU da última rodada.
  const fim = v.phase !== 'game_over' || pausa !== null ? null : assistindo
    ? <FimNaPlateia v={v} eu={eu} sala={sala} nomes={(t) => nomesDoTrio(t)} plateia={plateia!} />
    : <FimDePartida e={e} c={c as ConexaoMesa} v={v} sala={sala} eu={eu} lerSaldo={lerSaldo} lerPote={lerPote} lerConvite={lerConvite} gratis={gratis} assinar={assinar} />;
  const revanche = fim && !assistindo && sala.fase === 'fim' && sala.revanche ? <Revanche e={e} c={c as ConexaoMesa} sala={sala} agora={agora} gratis={gratis} /> : null;
  // Quem assiste vê que o trio está decidindo (o prazo da votação), sem os votos.
  const votoNaPlateia = assistindo ? e.prazos.find((p) => p.tipo === 'voto' && p.trio !== undefined) : undefined;

  // ---- partes da tela, nos dois arranjos ----
  const acoesDaFaixa = (
    <span className="acoes">
      {quantosAssistem > 0 && !assistindo && (
        <button className="chipbtn plateia" aria-label={`${quantosAssistem} ${quantosAssistem === 1 ? 'pessoa assistindo' : 'pessoas assistindo'}`}
          disabled={assistindo} onClick={() => setFolha(folha === 'plateia' ? null : 'plateia')}>
          <span aria-hidden="true">👀</span> {quantosAssistem}
        </button>
      )}
      {chipLimite}
      <button className="chipbtn icone" aria-label={pref.som ? 'Som ligado' : 'Som desligado'} onClick={() => setFolha(folha === 'som' ? null : 'som')}>
        {pref.som ? <IconeSom /> : <IconeMudo />}
      </button>
      <button className="chipbtn" onClick={() => setFolha(folha === 'regras' ? null : 'regras')}>Regras</button>
    </span>
  );
  const faixa = (
    <div className="abs strip">
      {assistindo ? (
        <span className="l assistindo"><span className="ao-vivo" aria-hidden="true" />Assistindo · <span className="brand">{sala.nome}</span></span>
      ) : sala.liga ? (
        <span className="l liga"><IconeTrofeu /><span className={sala.liga.marca ? 'brand' : undefined}>{sala.liga.nome}</span></span>
      ) : (
        <span className="l">{sala.nome || 'Mesa de amigos'} · <span className="brand">{sala.codigo}</span></span>
      )}
      {!pc && acoesDaFaixa}
    </div>
  );
  const placar = (
    <div className="abs board">
      <div className="team"><div className="tl"><b style={{ color: corDoTrio(meu, meu, assistindo) }}>{assistindo ? trioDaPlateia(sala.lugares, meu).toUpperCase() : 'NÓS'}</b>{listaDeNomes(meu)}</div><span className="pts" style={{ color: corDoTrio(meu, meu, assistindo) }}>{pad(v.scores[meu])}</span></div>
      <div className="mid">
        <span className={'val' + (quente ? ' hot' : '')}>{valor}</span>
        <div className="dots" aria-label={'Rodada ' + Math.min(3, h?.rounds.length ?? 1) + ' de 3'}>
          {[0, 1, 2].map((i) => {
            const rd = h?.rounds[i];
            const st: React.CSSProperties = rd?.result === meu ? { background: 'var(--nos-400)', borderColor: 'var(--nos-400)' }
              : rd?.result === deles ? { background: 'var(--eles-400)', borderColor: 'var(--eles-400)' }
                : rd?.result === 'tie' ? { background: 'var(--ink-300)', borderColor: 'var(--ink-300)' }
                  : rd && !h?.ended ? { borderColor: 'var(--gold-400)' } : {};
            return <i key={i} style={st} />;
          })}
        </div>
      </div>
      <div className="team r"><span className="pts" style={{ color: corDoTrio(deles, meu, assistindo) }}>{pad(v.scores[deles])}</span><div className="tl" style={{ alignItems: 'flex-end' }}><b style={{ color: corDoTrio(deles, meu, assistindo) }}>{assistindo ? trioDaPlateia(sala.lugares, deles).toUpperCase() : 'ELES'}</b>{listaDeNomes(deles)}</div></div>
      {gratis !== null && faixaDoLimite !== null && <FaixaLimite faixa={faixaDoLimite} aoAbrir={() => setFolha('limite')} />}
    </div>
  );
  const botaoEsconder = porQueAberta ? (
    // A regra não deixa esconder nesta rodada: o botão fica apagado e o toque explica o porquê.
    <>
      <button className="bb apagado esconder" aria-disabled="true" title={porQueAberta} aria-describedby="porque-aberta" onClick={() => avisar(porQueAberta)}>
        <IconeEsconder />Esconder
      </button>
      <span id="porque-aberta" className="so-leitor">{porQueAberta}</span>
    </>
  ) : (
    <button className={'bb esconder' + (esconder && podeEsconder ? ' on' : '')} disabled={!podeEsconder} aria-pressed={esconder && podeEsconder} onClick={() => setEsconder(!esconder)}>
      <IconeEsconder />Esconder
    </button>
  );
  // No computador, os atalhos 1, 2 e 3 embaixo das cartas e o Esconder embaixo do seu rosto.
  const extrasDaMao = pc && !assistindo && !naEspera ? (
    <>
      {!emVotacao && Array.from({ length: quantas }, (_, i) => {
        const p = posicaoNaMao(quantas, i, geo);
        return <kbd key={i} className="atalho-mao" aria-hidden="true" style={{ left: p[0] + CARTA_NA_MAO.computador[0] / 2 - 13, top: ATALHOS_COMPUTADOR }}>{i + 1}</kbd>;
      })}
      {!emVotacao && <div className="abs esconder-pc" style={{ left: LUG[0]![0] - 52, top: LUG[0]![1] + 54 }}>{botaoEsconder}</div>}
    </>
  ) : null;
  const mesaDesenhada = (
    <div className="abs tbl" style={{ ...estiloDaMesa(sala?.visual?.mesa), ...(pc ? { left: -PALCO_COMPUTADOR.x, top: -PALCO_COMPUTADOR.y } : {}) }}>
      <div className="abs hex" aria-hidden="true" style={{ left: 36, top: 182, width: 318, height: 368, background: 'var(--mesa-borda-escura)' }} />
      <div className="abs hex" aria-hidden="true" style={{ left: 40, top: 186, width: 310, height: 360, background: 'var(--mesa-borda)' }} />
      <div className="abs hex" aria-hidden="true" style={{ left: 52, top: 198, width: 286, height: 336, background: 'var(--mesa-friso)' }} />
      <div className="abs hex felt" aria-hidden="true" style={{ left: 54, top: 200, width: 282, height: 332 }} />
      {/* Espaço da marca no feltro, com até 10% de opacidade: o logo do patrocinador ou "Anuncie Aqui", no centro. */}
      <div className="abs watermark" aria-hidden="true">
        <MarcaNoFeltro marca={marca} />
      </div>
      {centro}
      {assentos}
      {meuAssento}
      {mao}
      {extrasDaMao}
      {dica}
      <div ref={caixaRef} className="voos" aria-hidden="true" />
      {emos.map((m) => {
        const c0 = LUG[m.rel]!, dir = c0[0] > 195 ? -1 : 1;
        return (
          <div key={m.id} className="emo" role="img" aria-label={m.rotulo} style={{ left: c0[0] + dir * 24, top: c0[1] - 26 }}>
            {m.emoji}
            {m.naipe && <span className={'suit' + (m.naipe === 'c' ? ' z' : m.naipe === 'h' || m.naipe === 'd' ? ' r' : '')}>{SIMBOLO[m.naipe]}</span>}
          </div>
        );
      })}
      {baloes.map((b) => {
        const c0 = LUG[b.rel]!;
        const baixo = c0[1] < 200; // quem senta no alto: o balão desce, longe do placar
        const [min, max] = pc ? [PALCO_COMPUTADOR.x + 92, PALCO_COMPUTADOR.x + PALCO_COMPUTADOR.largura - 92] : [92, (paisagem ? 980 : 390) - 92];
        return <div key={b.id} className={'balao ' + b.tipo + (b.nosso ? ' nos' : ' eles') + (baixo ? ' baixo' : '')} role="status"
          aria-label={b.rotulo} style={{ left: Math.min(Math.max(c0[0], min), max), top: c0[1] + (baixo ? 34 : -34) }}>{b.texto}</div>;
      })}
      {avisoMesa && (
        <div key={avisoMesa.id} className="aviso-mesa" role="status">
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M2 2l20 20" /><path d="M8.5 16.5a5 5 0 0 1 7 0" /><path d="M5 12.9a10 10 0 0 1 5.2-2.8" /><path d="M19 12.9a10 10 0 0 0-2.3-1.6" /><path d="M12 20h.01" /></svg>
          <span>{avisoMesa.texto}</span>
        </div>
      )}
      {naEspera && (
        <div className="abs espera-mao" role="status">
          <b>Você entra na próxima mão.</b>
          <span>Seu trio está com {v.scores[meu]}.</span>
        </div>
      )}
      {toast && !avisoMesa && <div className="toast" role="status">{toast.texto}</div>}
      {!toast && !avisoMesa && deles_decidindo && <div className="toast" role="status">{vot!.kind === 'onze' ? 'Eles decidem a mão de onze...' : 'Eles estão decidindo...'}</div>}
      {!toast && !avisoMesa && votoNaPlateia && <div className="toast" role="status">{trioDaPlateia(sala.lugares, votoNaPlateia.trio!)} {h?.special === 'onze' ? 'decide a mão de onze...' : 'está decidindo...'}</div>}
      {reacoesPlateia.length > 0 && (
        <div className="faixa-plateia" role="status" aria-label={'Plateia: ' + reacoesPlateia.map((r) => r.emoji).join(' ')}>
          <span className="rotulo-plateia">PLATEIA</span>
          {reacoesPlateia.map((r) => <span key={r.id} className="emo-plateia" aria-hidden="true">{r.emoji}</span>)}
        </div>
      )}
      {e.status === 'reconectando' && <div className="toast" role="status" style={{ top: 120 }}>Reconectando à mesa...</div>}
    </div>
  );
  const botaoTruco = (
    <button className={'abs cta' + (podeTruco ? ' pulse' : '')} disabled={!podeTruco} onClick={truco}
      style={pc ? { left: TRUCO_COMPUTADOR.x - PALCO_COMPUTADOR.x, top: TRUCO_COMPUTADOR.y - PALCO_COMPUTADOR.y, width: TRUCO_COMPUTADOR.largura, height: TRUCO_COMPUTADOR.altura } : undefined}>
      {rotulo} <small>{pequeno}</small>
    </button>
  );
  const momentoNaTela = momento && (
    <div className="moment" role="status" aria-label={momento.fala}>
      <b className={momento.cls}>{momento.grande}</b>
      {momento.linha && <span>{momento.linha}</span>}
      {momento.selo && (marca
        ? <div className="seal"><i>{verso.mono}</i>{marca.nome}</div>
        : <div className="seal anuncie">Anuncie Aqui</div>)}
    </div>
  );

  if (pc) {
    // Computador (tela 5.5 do canvas): grade de 3 colunas na janela toda. Esquerda: placar, histórico da mão e seu
    // trio. Centro: a mesa e a sua mão, com o TRUCO ao lado e a votação no lugar dele. Direita: Sinais e Reações
    // sempre abertos e os atalhos; o cartão do jogador e o menu abrem nessa coluna, sem cobrir a mesa.
    const vez = h && v.phase === 'play' && !h.ended && pausa === null && !dist ? h.turn : null;
    const sinaisDaMao = sinaisRecebidos.filter((x) => x.mao === v.handNo);
    return (
      <div className="mesa-pc">
        <aside className="pc-esq">
          {faixa}
          {placar}
          <HistoricoDaMao h={h} eu={eu} nome={nome} meu={meu} sinais={sinaisDaMao} vez={vez} segundos={vez === eu ? meusSegundos : null} assistindo={assistindo} />
          {!assistindo && <SeuTrio h={h} eu={eu} meu={meu} nome={nome} lugares={sala.lugares} vez={vez} />}
        </aside>
        <main className="pc-centro" ref={centroRef}>
          <div className="pc-palco" style={{ width: PALCO_COMPUTADOR.largura, height: PALCO_COMPUTADOR.altura, transform: `scale(${escalaDoCentro})`, '--compensa': Math.min(Math.max(1, 1 / escalaDoCentro), 1.4).toFixed(3) } as React.CSSProperties}>
            {mesaDesenhada}
            {!assistindo && !emVotacao && botaoTruco}
            {!assistindo && !emVotacao && (
              <kbd className="atalho-mao" aria-hidden="true"
                style={{ left: TRUCO_COMPUTADOR.x - PALCO_COMPUTADOR.x + TRUCO_COMPUTADOR.largura / 2 - 13, top: ATALHOS_COMPUTADOR - PALCO_COMPUTADOR.y }}>T</kbd>
            )}
            {doca}
          </div>
        </main>
        <aside className="pc-dir">
          <div className="pc-topo">
            {chipLimite}
            {quantosAssistem > 0 && (
              <button className="assistindo-pc" disabled={assistindo} onClick={() => setFolha(folha === 'plateia' ? null : 'plateia')}
                aria-label={`${quantosAssistem} ${quantosAssistem === 1 ? 'pessoa assistindo' : 'pessoas assistindo'}`}>
                <span aria-hidden="true">👀</span> {quantosAssistem} assistindo
              </button>
            )}
            <button className="menu-pc" aria-label="Menu da mesa" aria-expanded={folha === 'menu'} onClick={() => setFolha(folha === 'menu' ? null : 'menu')}>
              <IconeMenu />
            </button>
          </div>
          {assistindo ? (
            <>
              <section className="painel" aria-label="Reações da plateia">
                <header><h2>Reagir</h2><span>Aparece na faixa Plateia, para a mesa e para quem assiste.</span></header>
                {painelReacoesPlateia}
              </section>
              <div className="pc-plateia-acoes">
                {plateia!.podeSentar && <button className="btn1" onClick={plateia!.aoSentar}>SENTAR NA MESA</button>}
                <button className="btn2" onClick={plateia!.aoSair}>Sair</button>
              </div>
            </>
          ) : (
            <>
              <section className="painel sinais" aria-label="Sinais">
                <header><h2>Sinais</h2><span>{sala.regras.sinais === 'desligados' ? 'Desligados nesta sala.' : 'Fixos: valem em toda mão.'}</span></header>
                {sala.regras.sinais !== 'desligados' && painelSinais}
              </section>
              <section className="painel reacoes" aria-label="Reações">
                <header><h2>Reações</h2><span>Todo mundo na mesa vê, inclusive o trio adversário.</span></header>
                {painelReacoes}
              </section>
              <ul className="atalhos-pc" aria-label="Atalhos do teclado">
                <li><kbd>1</kbd><kbd>2</kbd><kbd>3</kbd> jogam a carta</li>
                <li><kbd>T</kbd> pede truco</li>
              </ul>
            </>
          )}
          {conteudoFolha}
        </aside>
        {momentoNaTela}
        {fim}
        {revanche}
        {prazoEntrada && sala.liga && <Entrada liga={sala.liga} ate={prazoEntrada.ate} agora={agora} />}
      </div>
    );
  }

  return (
    <>
      <div className="glow" style={{ left: -60, top: 90, width: 220, height: 220, background: 'radial-gradient(circle,rgba(242,193,78,.16),rgba(242,193,78,0) 70%)' }} />
      <div className="glow" style={{ left: 250, top: 40, width: 200, height: 200, background: 'radial-gradient(circle,rgba(242,193,78,.12),rgba(242,193,78,0) 70%)' }} />
      {faixa}
      {placar}
      {mesaDesenhada}
      {assistindo ? (
        <div className="abs bar plateia-bar">
          <button className={'bb' + (folha === 'reacoes-plateia' ? ' on' : '')} onClick={() => setFolha(folha === 'reacoes-plateia' ? null : 'reacoes-plateia')}>
            <IconeSorriso />Reagir
          </button>
          {plateia!.podeSentar && <button className="btn1 sentar-plateia" onClick={plateia!.aoSentar}>SENTAR NA MESA</button>}
          <button className="bb" onClick={plateia!.aoSair}><IconeSair />Sair</button>
        </div>
      ) : (
        <>
        {botaoTruco}
        <div className={'abs bar' + (emVotacao ? ' votando' : '')}>
          <button className={'bb' + (folha === 'sinais' ? ' on' : '')} disabled={!sinaisAtivos} onClick={() => setFolha(folha === 'sinais' ? null : 'sinais')}>
            <IconeOlho />Sinais
          </button>
          <button className={'bb' + (folha === 'reacoes' ? ' on' : '')} onClick={() => setFolha(folha === 'reacoes' ? null : 'reacoes')}>
            <IconeSorriso />Reações
          </button>
          {!emVotacao && botaoEsconder}
        </div>
        </>
      )}
      {doca}
      {conteudoFolha && <><div className="scrim" onClick={() => setFolha(null)} />{conteudoFolha}</>}
      {momentoNaTela}
      {fim}
      {revanche}
      {prazoEntrada && sala.liga && <Entrada liga={sala.liga} ate={prazoEntrada.ate} agora={agora} />}
    </>
  );
}

// A barra de "Entrando na mesa" anda no tempo que faltava quando a tela abriu.
function Entrada({ liga, ate, agora }: { liga: NonNullable<SalaPublica['liga']>; ate: number; agora: number }) {
  const [ms] = useState(() => Math.max(0, ate - agora));
  return <EntradaPatrocinada liga={liga} ms={ms} semana={{ inicio: liga.inicio, fim: liga.fim, premio: liga.premio, agora }} />;
}

// Fim da partida para quem assiste: o placar dos dois trios e quem venceu. Sem ganhos nem revanche.
function FimNaPlateia({ v, eu, sala, nomes, plateia }: { v: PlayerView; eu: Seat; sala: SalaPublica; nomes: (t: Team) => string[]; plateia: ModoPlateia }) {
  const a = team(eu), b = (1 - a) as Team, venceu = v.winner;
  return (
    <div className="full" role="dialog" aria-label="Fim da partida">
      <h1 style={{ color: 'var(--gold-400)' }}>FIM DE PARTIDA</h1>
      <div className="trios">
        <div className="trio"><b style={{ color: 'var(--nos-400)' }}>{trioDaPlateia(sala.lugares, a).toUpperCase()}</b><span className="pts" style={{ color: 'var(--nos-400)' }}>{pad(v.scores[a])}</span></div>
        <div className="trio" style={{ alignItems: 'flex-end' }}><b style={{ color: 'var(--eles-400)' }}>{trioDaPlateia(sala.lugares, b).toUpperCase()}</b><span className="pts" style={{ color: 'var(--eles-400)' }}>{pad(v.scores[b])}</span></div>
      </div>
      {venceu !== null && <p className="sub">{trioDaPlateia(sala.lugares, venceu)} venceu: {nomes(venceu).join(', ')}.</p>}
      {plateia.podeSentar && <button className="btn1" onClick={plateia.aoSentar}>SENTAR NA MESA</button>}
      <button className="btn2" onClick={plateia.aoSair}>Sair</button>
    </div>
  );
}

// Rei do Pote no fim da partida valendo Tentos: a posição da semana pela API, depois da liquidação.
export type LerPote = (codigo: string, partida: number) => Promise<PoteDaPartida>;
type PropsDoFim = {
  e: EstadoMesa; c: ConexaoMesa; v: PlayerView; sala: SalaPublica; eu: Seat; lerSaldo?: () => Promise<number>; lerPote?: LerPote;
  lerConvite?: () => Promise<string | null>;
  gratis?: number | null; assinar?: () => Promise<void>; // quem está no Grátis: as partidas que sobram hoje
};

// O nível de Rodagem ao lado do rosto (pessoas; bot não tem).
function NivelNoRosto({ l }: { l: LugarPublico | null }) {
  if (l?.tipo !== 'pessoa' || l.bot || !l.nivel) return null;
  return <span className="nivel-rosto" aria-label={`Nível ${l.nivel}`}>{l.nivel}</span>;
}

// O que a partida rendeu de Rodagem (vem com os ganhos, depois do registro), com o dia da ofensiva por cima.
function RodagemDoFim({ e }: Pick<PropsDoFim, 'e'>) {
  const g = e.ganhos?.partida === e.partida ? e.ganhos.ganhos : null;
  return (
    <>
      {g?.ofensiva && <OfensivaDaPartida o={g.ofensiva} partida={g.partida} />}
      {g?.rodagem && <RodagemDaPartida r={g.rodagem} partida={g.partida} />}
      {g?.missoes && <MissoesDaPartida ms={g.missoes} partida={g.partida} />}
      {g?.passe && <PasseDaPartida x={g.passe} partida={g.partida} />}
    </>
  );
}

// Badges que a partida deu (vêm com os ganhos, depois do registro), com o card de compartilhar.
function BadgesDaPartida({ e, sala, eu, lerConvite }: Pick<PropsDoFim, 'e' | 'sala' | 'eu' | 'lerConvite'>) {
  const g = e.ganhos?.partida === e.partida ? e.ganhos.ganhos : null, l = sala.lugares[eu];
  if (!g?.badges.length) return null;
  return <BadgesNoFim ganhos={g.badges} partida={g.partida} apelido={l?.tipo === 'pessoa' ? l.perfil.apelido : 'Você'} lerConvite={lerConvite} />;
}

// A próxima ação em destaque no fim (item 16): revanche com os mesmos 6, missão que falta 1 partida, nível ou passe
// quase cheio, Rodada quente ou jogar de novo na liga (com a liga oculta, ver as salas abertas). Um botão de ação só
// (CTA), o resto é texto.
function ProximaAcaoNoFim({ e, c, sala, gratis = null }: Pick<PropsDoFim, 'e' | 'c' | 'sala'> & { gratis?: number | null }) {
  const g = e.ganhos?.partida === e.partida ? e.ganhos.ganhos : null;
  const seis = sala.lugares.filter((l) => l?.tipo === 'pessoa' && !l.bot).length === 6;
  if (!g && !sala.revanche && sala.fase !== 'encerrada') return null;
  const a = acaoDoFim(!!sala.revanche && seis, g?.proxima);
  // Liga oculta (LIGA_DA_SEMANA): o fim leva às salas abertas, e não à busca de mesa.
  const liga = ligaNoAr() && a.tipo !== 'salas';
  // A revanche que gasta a última partida do Grátis avisa no próprio convite (item 68).
  const [titulo, sub] = a.tipo === 'revanche' ? ['Revanche com os mesmos 6', gratis === 1 ? 'Responda no centro da mesa. Vale a sua última grátis de hoje.' : 'Responda no centro da mesa.']
    : a.tipo === 'missao' ? [`Falta 1 partida para fechar a missão`, `${a.titulo} · +${a.rodagem} de Rodagem`]
      : a.tipo === 'nivel' ? [`Falta 1 partida para o nível ${a.nivel}`, 'A Rodagem da próxima partida fecha a barra.']
        : a.tipo === 'passe' ? [`Falta 1 partida para o nível ${a.nivel} do passe`, 'Uma partida terminada vale 1 estrela.']
          : a.tipo === 'quente' ? [`Emende a próxima: +${a.bonus}%`, 'Comece em até 10 minutos para manter a Rodada quente.']
            : liga ? [sala.liga ? 'Bora mais uma?' : 'Que tal a Liga da Semana?', 'Busca de mesa pela sua divisão.']
              : ['Bora mais uma?', 'Escolha a próxima mesa na lista de salas abertas.'];
  return (
    <section className={'proxima-acao ' + a.tipo} aria-label="Próxima ação">
      <b>{titulo}</b>
      <small>{sub}</small>
      {a.tipo !== 'revanche' && (
        <button className="btn1" onClick={() => { c.enviar({ t: 'sair' }); c.encerrar(); ir(liga ? { tela: 'liga', trio: null } : destinoAoSairDaMesa()); }}>
          {!liga ? 'VER SALAS ABERTAS' : sala.liga ? 'JOGAR DE NOVO NA LIGA' : 'JOGAR NA LIGA'}
        </button>
      )}
    </section>
  );
}

// Sala de campeonato: o confronto no lugar da próxima ação. No melhor de 3, a próxima partida começa sozinha.
function CampeonatoNoFim({ e, c, sala }: Pick<PropsDoFim, 'e' | 'c' | 'sala'>) {
  useAgora();
  const camp = sala.campeonato!, inicio = e.prazos.find((p) => p.tipo === 'inicio'), vencedor = vencedorDoConfronto(camp);
  const s = inicio ? Math.max(0, Math.ceil((inicio.ate - agoraNoServidor(e)) / 1000)) : null;
  return (
    <section className="campeonato-fim" aria-label="Campeonato">
      <b>{camp.nome} · {camp.rodada}</b>
      {camp.melhorDe === 3 && <small>Melhor de 3: {camp.trios[0]} {camp.vitorias[0]} x {camp.vitorias[1]} {camp.trios[1]}</small>}
      <small>{vencedor ? `Jogo decidido: vitória de ${vencedor}. A tabela já andou.` : s !== null ? `A próxima partida começa em ${s}s.` : 'A próxima partida começa em instantes.'}</small>
      {vencedor && <button className="btn1" onClick={() => { c.enviar({ t: 'sair' }); c.encerrar(); ir({ tela: 'campeonato', slug: camp.slug }); }}>VER O CAMPEONATO</button>}
    </section>
  );
}

function FimDePartida({ e, c, v, sala, eu, lerSaldo, lerPote, lerConvite, gratis, assinar }: PropsDoFim) {
  const meu = team(eu), venceu = v.winner === meu;
  useEffect(() => { if (venceu) som.vitoria(); else som.derrota(); }, [venceu]);
  useEffect(() => {
    medirUmaVez(`fim:${sala.codigo}:${e.partida}`, 'partida_terminada', { resultado: venceu ? 'nos' : 'eles', pontos_nos: v.scores[meu], pontos_eles: v.scores[1 - meu] });
  }, [e.partida]);
  if (sala.tentos) return <FimValendoTentos e={e} c={c} v={v} sala={sala} eu={eu} lerSaldo={lerSaldo} lerPote={lerPote} lerConvite={lerConvite} />;
  // Deitado e no computador, o fim é sempre em duas colunas: o placar à esquerda e o que a partida rendeu, a próxima
  // ação e quem estava na mesa à direita. Em pé, uma coluna só.
  return (
    <div className="full fim-colunas" role="dialog" aria-label="Fim da partida">
      <div className="fim-col">
      <h1 style={{ color: venceu ? 'var(--gold-400)' : 'var(--ink-050)' }}>{venceu ? 'VITÓRIA!' : 'NÃO FOI DESSA VEZ'}</h1>
      <div className="trios">
        <div className="trio"><b style={{ color: 'var(--nos-400)' }}>NÓS</b><span className="pts" style={{ color: 'var(--nos-400)' }}>{pad(v.scores[meu])}</span></div>
        <div className="trio" style={{ alignItems: 'flex-end' }}><b style={{ color: 'var(--eles-400)' }}>ELES</b><span className="pts" style={{ color: 'var(--eles-400)' }}>{pad(v.scores[1 - meu]!)}</span></div>
      </div>
      <p className="sub">{venceu ? 'Nós ganhamos a partida.' : 'Eles ganharam a partida.'}</p>
      <LigaNoFim ganhos={e.ganhos?.partida === e.partida ? e.ganhos.ganhos : null} />
      </div>
      <div className="fim-col">
      {/* Sem partidas grátis hoje: o cartão do limite no lugar da próxima ação (a revanche fica desabilitada). Com 1
          sobrando, o cartão "Só resta 1" no topo, acima da revanche; com 2 ou mais, a linha embaixo dela. */}
      {sala.campeonato ? <CampeonatoNoFim e={e} c={c} sala={sala} />
        : gratis != null && gratis <= 0 ? <LimiteNoFim restantes={0} lerConvite={lerConvite} assinar={assinar} />
          : <>
              {gratis === 1 && <LimiteNoFim restantes={1} lerConvite={lerConvite} assinar={assinar} />}
              <ProximaAcaoNoFim e={e} c={c} sala={sala} gratis={gratis} />
            </>}
      {!sala.campeonato && gratis != null && gratis >= 2 && <LimiteNoFim restantes={gratis} lerConvite={lerConvite} />}
      <RodagemDoFim e={e} />
      <BadgesDaPartida e={e} sala={sala} eu={eu} lerConvite={lerConvite} />
      <PessoasDaMesa lugares={sala.lugares} eu={eu} nomeDe={(s) => nomeNoLugar(sala, eu, s as Seat)} />
      {sala.fase === 'encerrada' && (
        <p className="sub">{sala.campeonato ? 'Este jogo do campeonato acabou.' : sala.liga ? 'Esta mesa da liga fechou.' : sala.removida ? 'A sala foi encerrada na revisão do nome.' : 'O dono saiu e a sala foi encerrada.'}</p>
      )}
      <button className="btn2" onClick={() => { c.enviar({ t: 'sair' }); c.encerrar(); ir(destinoAoSairDaMesa()); }}>Sair da mesa</button>
      {e.anonimo && <p className="sub">Guarde sua conta com Google no Perfil para criar suas próprias salas.</p>}
      </div>
    </div>
  );
}

// Fim da partida valendo Tentos (canvas T05 e 8.5): o que entrou e saiu do seu saldo, a linha do Rei do Pote, o pote
// e quem do trio campeão recebeu. A revanche vem por cima, no modal (Sim reserva o ingresso de novo).
function FimValendoTentos({ e, c, v, sala, eu, lerSaldo, lerPote, lerConvite, gratis, assinar }: PropsDoFim) {
  const t = sala.tentos!, meu = team(eu), venceu = v.winner === meu, u = e.usuario ?? '';
  const r = t.resultado?.partida === e.partida ? t.resultado : null;
  const [saldo, setSaldo] = useState<number | null>(null);
  const [pote, setPote] = useState<PoteDaPartida | null>(null);
  useEffect(() => { if (r && lerSaldo) lerSaldo().then(setSaldo, () => {}); }, [r?.partida, r?.anulada]);
  useEffect(() => {
    let vivo = true;
    if (r && !r.anulada && lerPote) void comPrazo(lerPote(sala.codigo, r.partida)).then((x) => { if (vivo) setPote(x); });
    return () => { vivo = false; };
  }, [r?.partida, r?.anulada]);
  const recebi = !!r && r.recebem.includes(u), saiu = t.saiu.includes(u);
  const ganhou = (s: Seat) => team(s) === v.winner;
  let destaque: React.ReactNode;
  if (!r) destaque = <p className="sub" role="status">Calculando o prêmio...</p>;
  else if (r.anulada) destaque = <p className="sub" role="status">Partida anulada. Os ingressos voltaram para o saldo.</p>;
  else {
    const valor = recebi ? `+${r.premio}` : `−${t.ingresso}`;
    destaque = (
      <div className={'resultado-tentos' + (recebi ? ' ganhou' : '')}>
        <TentoMesa tamanho={40} />
        <p>
          <b>{valor} TENTOS</b>
          <span>
            {venceu && saiu ? 'Você saiu no meio da partida e perdeu a parte do prêmio. ' : ''}
            Você entrou com {t.ingresso}.{saldo !== null ? <> Saldo agora: <b>{saldo}</b></> : null}
          </span>
          {/* O saldo de mesa da partida já se sabe aqui; a posição da semana vem da API (até 3 s). Leva à aba. */}
          <button className="linha-pote-fim" onClick={() => { c.enviar({ t: 'sair' }); c.encerrar(); ir({ tela: 'ranking', tipo: 'pote' }); }}>
            <Coroa tamanho={14} />{linhaDoPote(recebi ? r.premio - t.ingresso : -t.ingresso, pote)}
          </button>
        </p>
      </div>
    );
  }
  const nos = venceu ? `+${t.premio}` : `−${t.ingresso}`, eles = venceu ? `−${t.ingresso}` : `+${t.premio}`;
  return (
    <div className="full fim-tentos" role="dialog" aria-label="Fim da partida valendo Tentos">
      <div className="fim-col">
      <div className="sala-fim"><span><TentoMesa tamanho={16} />{sala.nome} · valendo Tentos</span><span>{t.ingresso} por jogador</span></div>
      <h1 style={{ color: venceu ? 'var(--gold-400)' : 'var(--ink-050)' }}>{venceu ? 'VITÓRIA!' : 'NÃO FOI DESSA VEZ'}</h1>
      <div className="trios">
        <div className="trio"><b style={{ color: 'var(--nos-400)' }}>NÓS</b><span className="pts" style={{ color: 'var(--nos-400)' }}>{pad(v.scores[meu])}</span></div>
        <div className="trio" style={{ alignItems: 'flex-end' }}><b style={{ color: 'var(--eles-400)' }}>ELES</b><span className="pts" style={{ color: 'var(--eles-400)' }}>{pad(v.scores[1 - meu]!)}</span></div>
      </div>
      {destaque}
      <LigaNoFim ganhos={e.ganhos?.partida === e.partida ? e.ganhos.ganhos : null} />
      </div>
      <div className="fim-col">
      {/* O limite do Grátis também vale aqui (item 68): sem partidas, o cartão do limite; com 1, o "Só resta 1". */}
      {gratis != null && gratis <= 0 ? <LimiteNoFim restantes={0} lerConvite={lerConvite} assinar={assinar} />
        : <>
            {gratis === 1 && <LimiteNoFim restantes={1} lerConvite={lerConvite} assinar={assinar} />}
            <ProximaAcaoNoFim e={e} c={c} sala={sala} gratis={gratis} />
          </>}
      {gratis != null && gratis >= 2 && <LimiteNoFim restantes={gratis} lerConvite={lerConvite} />}
      {r && !r.anulada && (
        <>
          <div className="pote-fim" aria-label={`Pote ${t.pote}, casa ${t.casa}, cada vencedor leva ${t.premio}, cada perdedor deixou ${t.ingresso}`}>
            <div><span>POTE</span><b>{t.pote}</b></div>
            <div><span>CASA</span><b>{t.casa}</b></div>
            <div><span>NÓS</span><b className={venceu ? 'dourado' : undefined}>{nos}</b></div>
            <div><span>ELES</span><b className={venceu ? undefined : 'dourado'}>{eles}</b></div>
          </div>
          <ul className="campeoes" aria-label="Prêmio do trio campeão">
            {sala.lugares.map((l, i) => {
              if (!l || l.tipo !== 'pessoa' || !ganhou(i as Seat)) return null;
              const pago = r.recebem.includes(l.id), voce = l.id === u;
              const obs = t.saiu.includes(l.id) ? 'Saiu da partida: a parte foi queimada' : l.bot ? `Caiu, ${l.botApelido ?? 'o bot'} jogou no lugar` : null;
              return (
                <li key={l.id}>
                  <span className="nome-campeao"><b>{voce ? 'Você' : l.perfil.apelido}</b>{obs && <small>{obs}</small>}</span>
                  <b className={pago ? 'dourado' : 'apagado'}>{pago ? `+${r.premio}` : '0'}</b>
                </li>
              );
            })}
          </ul>
        </>
      )}
      {sala.fase === 'encerrada' && <p className="sub">{sala.removida ? 'A sala foi encerrada na revisão do nome.' : 'O dono saiu e a sala foi encerrada.'}</p>}
      <RodagemDoFim e={e} />
      <BadgesDaPartida e={e} sala={sala} eu={eu} lerConvite={lerConvite} />
      <PessoasDaMesa lugares={sala.lugares} eu={eu} nomeDe={(s) => nomeNoLugar(sala, eu, s as Seat)} />
      {e.aviso && !sala.revanche && <p className="sub erro-fim" role="alert">{e.aviso.mensagem}</p>}
      <button className="btn2" onClick={() => { c.enviar({ t: 'sair' }); c.encerrar(); ir(destinoAoSairDaMesa()); }}>Sair da mesa</button>
      <p className="sub">Quem sai no meio da partida perde a parte do prêmio.</p>
      </div>
    </div>
  );
}

// Revanche (fim da partida): modal grande e centralizado sobre o fundo escuro, com o cronômetro e Sim (verde) ou
// Não (vermelho). Valendo Tentos, o Sim reserva o ingresso. Quem diz Não levanta da mesa e fica na sala. Depois do
// Sim, uma faixa no rodapé mostra quantos confirmaram; o dono pode começar antes do prazo.
// gratis: as partidas que sobram hoje para quem está no Grátis (null para quem assina). Sem nenhuma, não confirma a
// revanche (é partida nova) e tem o link "Ver como jogar mais"; com 1, a linha de que esta é a última (item 68).
function Revanche({ e, c, sala, agora, gratis = null }: { e: EstadoMesa; c: ConexaoMesa; sala: SalaPublica; agora: number; gratis?: number | null }) {
  const r = sala.revanche!, t = sala.tentos, u = e.usuario ?? '';
  const semPartidas = gratis !== null && gratis <= 0, ultima = gratis === 1;
  const [enviado, setEnviado] = useState(false);
  useEffect(() => { if (ultima) avisoDoLimiteVisto('revanche'); }, [ultima]);
  useEffect(() => { setEnviado(false); }, [r.sim.length, e.aviso?.em]); // resposta do servidor: libera os botões
  const restaMs = Math.max(0, r.ate - agora), resta = Math.ceil(restaMs / 1000);
  const sentados = sala.lugares.filter((l) => l?.tipo === 'pessoa').length;
  if (r.sim.includes(u)) {
    return (
      <div className="faixa-revanche" role="status">
        <p><b>Revanche confirmada</b><span>{r.sim.length} de {sentados} · {resta} s</span></p>
        {u === sala.dono && <button className="btn1" onClick={() => c.enviar({ t: 'iniciar' })}>COMEÇAR AGORA</button>}
      </div>
    );
  }
  const responder = (sim: boolean) => { setEnviado(true); c.enviar({ t: 'revanche', sim }); };
  const volta = 2 * Math.PI * 52, falta = Math.min(1, restaMs / (PRAZO_REVANCHE_S * 1000));
  return (
    <div className="revanche-fundo">
      <div className="revanche" role="alertdialog" aria-modal="true" aria-labelledby="titulo-revanche" aria-describedby="texto-revanche">
        <h2 id="titulo-revanche">REVANCHE?</h2>
        <div className={'relogio-revanche' + (resta <= 5 ? ' acabando' : '')} role="timer" aria-label={`${resta} segundos para responder`}>
          <svg viewBox="0 0 120 120" aria-hidden="true">
            <circle className="trilho" cx="60" cy="60" r="52" />
            <circle className="arco" cx="60" cy="60" r="52" strokeDasharray={volta} strokeDashoffset={volta * (1 - falta)} />
          </svg>
          <span><b>{resta}</b><small>s</small></span>
        </div>
        <p id="texto-revanche">
          {t ? `Sim reserva o ingresso de ${t.ingresso} Tentos.` : 'A mesma mesa, mais uma partida.'}
          {sala.liga ? ' Se alguém disser Não ou não responder, a mesa fecha.' : ' Quem não responder levanta da mesa.'}
        </p>
        {ultima && <p className="ultima-revanche">Esta revanche usa a sua última grátis de hoje</p>}
        {e.aviso && <p className="erro" role="alert">{e.aviso.mensagem}</p>}
        <div className="botoes-revanche">
          <button className="sim" disabled={enviado || semPartidas} autoFocus={!semPartidas} onClick={() => responder(true)}>
            {semPartidas ? 'Sem partidas grátis hoje' : t ? `SIM · ${t.ingresso}` : 'SIM'}
          </button>
          <button className="nao" disabled={enviado} onClick={() => responder(false)}>NÃO</button>
        </div>
        {/* Sem partidas: sai da mesa (não joga a revanche) e abre a página do limite. */}
        {semPartidas && <button className="link" onClick={() => { c.enviar({ t: 'sair' }); c.encerrar(); ir({ tela: 'limite' }); }}>Ver como jogar mais</button>}
      </div>
    </div>
  );
}

function TentoMesa({ tamanho }: { tamanho: number }) {
  return (
    <svg width={tamanho} height={tamanho} viewBox="0 0 24 24" aria-hidden="true" style={{ flex: 'none' }}>
      <path d="M12 2.5c3.6 2.6 5.6 6 5.6 9.5s-2.2 7.2-5.6 9.5c-3.4-2.3-5.6-6-5.6-9.5s2-6.9 5.6-9.5z" fill="var(--gold-400)" stroke="var(--gold-600)" strokeWidth="1.3" />
      <path d="M12 6v12" stroke="var(--gold-600)" strokeWidth="1.2" strokeLinecap="round" />
    </svg>
  );
}

function IconeSom() {
  return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M11 5 6 9H3v6h3l5 4z" /><path d="M15.5 8.5a5 5 0 0 1 0 7M18.5 5.5a9 9 0 0 1 0 13" /></svg>;
}
function IconeMudo() {
  return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M11 5 6 9H3v6h3l5 4z" /><path d="m16 9 5 6M21 9l-5 6" /></svg>;
}
function IconeOlho() {
  return <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z" /><circle cx="12" cy="12" r="3" /></svg>;
}
function IconeSair() {
  return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M15 4h4v16h-4" /><path d="M10 8l-4 4 4 4" /><path d="M6 12h10" /></svg>;
}
function IconeSorriso() {
  return <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="9" /><path d="M9 10h.01M15 10h.01M8.5 14.5c2 2 5 2 7 0" /></svg>;
}
function IconeEsconder() {
  return <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="5" y="3" width="14" height="18" rx="2" /><path d="M9 9l6 6M15 9l-6 6" /></svg>;
}
function IconeMenu() {
  return <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true"><path d="M4 7h16M4 12h16M4 17h16" /></svg>;
}
